import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from "react";
import { useNavigate } from "react-router-dom";
import { ChuziApiError, isInsufficientCreditsError } from "@chuzi/shared/api";
import type {
  AiSceneMode,
  StoryListItem,
  SceneListItem,
  SceneChoice,
  SceneMapEntry,
  TextLine,
  RealmId,
  LineType,
  LogicOperation,
  UserProfile,
} from "@chuzi/shared/types";
import { t, resolveSceneTextFont } from "@chuzi/shared/config";
import { getThemeTokens, type RealmThemeTokens } from "@chuzi/shared/themes";
import { api } from "../lib/api";
import { isUninitializedStory } from "../lib/story-scaffold";
import {
  buildSpendingModalRequest,
  handleInsufficientCredits,
  useSpendingModal,
} from "../lib/spending-modal";
import { useCloseAllModalsListener } from "../lib/close-all-modals";
import { VideoPlayer } from "./video-player";
import { DefaultSceneSlate } from "./default-scene-slate";
import { ChoiceFlashArrow } from "./choice-flash-arrow";
import {
  RichTextLineEditor,
  FormattingToolbar,
  TextLineList,
  TextPreviewPanel,
  LineTimingControls,
  SCROLLABLE_TEXT_FILL,
  createDefaultLine,
  computeTotalDuration,
  formatMs,
  resolveChoiceTiming,
  computeChoiceOpacity,
  choiceTimingToLine,
  timingToChoiceSeconds,
  computeEffectiveSceneDuration,
  DEFAULT_SCENE_FONT_SIZE,
  normalizeSceneFontSize,
  previewMsWhenVisible,
} from "./generator-timeline";
import {
  choicesFromSceneActions,
  DEFAULT_SLATE_DURATION_MS,
  resolveSceneChoices,
  sceneShowsDefaultSlateWithLines,
  resolveEditorPreviewChoices,
  isViewerExitChoice,
  isViewerExitSuppressionMarker,
  VIEWER_EXIT_CHOICE_ID,
  VIEWER_EXIT_SUPPRESSED_ID,
} from "../lib/scene-viewer-data";
import { SceneTransmissionStage } from "./scene-transmission-stage";
import { ChuziAiWizard } from "./chuzi-ai-wizard";
import { GeneratorEntry } from "./generator-entry";
import { StoryGenerator } from "./story-generator";
import { SpeechTextarea } from "./speech-textarea";

type NodeType = "text" | "video" | "sound" | "music" | "image" | "choice" | "credits" | "logic";
type DeleteState = "idle" | "confirming";
type CreateSceneFlow = null | "choice" | "blank" | "upstream" | "downstream";
type SceneMediaMode = "text" | "imagery" | "film";
const CREATE_SCENE_OPTION = "__chuzi_create_scene__";
/** Coalesce rapid editor saves into fewer PATCH /scenes calls. */
const SCENE_AUTOSAVE_DEBOUNCE_MS = 1500;
/** Debounce choice timing/layout writes during slider drags. */
const CHOICE_AUTOSAVE_DEBOUNCE_MS = 800;

function normalizeLoadedTextLines(lines: TextLine[]): TextLine[] {
  const textOnly = lines.filter((line) => !line.type || line.type === "text");
  if (textOnly.length !== 1) return lines;
  const [line] = textOnly;
  if (line.height_pct != null && line.height_pct > 0) return lines;
  return lines.map((entry) =>
    entry.id === line.id ? { ...entry, ...SCROLLABLE_TEXT_FILL } : entry,
  );
}

const DEFAULT_TEXT_COLOR = "#1a1a2e";
const DEFAULT_TEXT_BACKGROUND = "#f5f0e8";

type TextAuthoringPayload = {
  sceneId: string;
  mediaMode: SceneMediaMode;
  lines: TextLine[];
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
  fontSize: number;
};

function textAuthoringPayloadToScenePatch(payload: TextAuthoringPayload): Record<string, unknown> {
  const html = payload.lines
    .filter((l) => !l.type || l.type === "text")
    .map((l) => l.html)
    .join("");
  return {
    media_mode: payload.mediaMode,
    text_content: { html, lines: payload.lines },
    text_style: {
      font_family: payload.fontFamily,
      text_color: payload.textColor,
      background_color: payload.backgroundColor,
      font_size: payload.fontSize,
    },
  };
}

interface StoryChoiceArrowDefaults {
  x: number;
  y: number;
  w: number;
  h: number;
  arrow_rotation: number;
  arrow_scale: number;
}

const STORY_CHOICE_ARROW_DEFAULTS: StoryChoiceArrowDefaults = {
  x: 82,
  y: 78,
  w: 12,
  h: 14,
  arrow_rotation: 90,
  arrow_scale: 1,
};

function storyChoiceArrowDefaultsKey(storyId: string) {
  return `chuzi:choiceArrowDefaults:${storyId}`;
}

function loadStoryChoiceArrowDefaults(storyId: string): StoryChoiceArrowDefaults {
  try {
    const raw = localStorage.getItem(storyChoiceArrowDefaultsKey(storyId));
    if (!raw) return STORY_CHOICE_ARROW_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<StoryChoiceArrowDefaults>;
    return { ...STORY_CHOICE_ARROW_DEFAULTS, ...parsed };
  } catch {
    return STORY_CHOICE_ARROW_DEFAULTS;
  }
}

function persistStoryChoiceArrowDefaults(storyId: string, choice: SceneChoice) {
  try {
    localStorage.setItem(
      storyChoiceArrowDefaultsKey(storyId),
      JSON.stringify({
        x: choice.x,
        y: choice.y,
        w: choice.w,
        h: choice.h,
        arrow_rotation: choice.arrow_rotation,
        arrow_scale: choice.arrow_scale,
      } satisfies StoryChoiceArrowDefaults),
    );
  } catch { /* quota — non-critical */ }
}

/** Full scene-action properties blob — PATCH replaces the whole object, never partial keys. */
function choiceToActionProperties(choice: SceneChoice): Record<string, unknown> {
  return {
    label: choice.label,
    target_scene_id: choice.target_scene_id,
    x: choice.x,
    y: choice.y,
    w: choice.w,
    h: choice.h,
    arrow_rotation: choice.arrow_rotation,
    arrow_scale: choice.arrow_scale,
    fade_in_ms: choice.fade_in_ms,
    fade_out_ms: choice.fade_out_ms,
    duration_ms: choice.duration_ms,
    start_time_seconds: choice.start_time_seconds,
    end_time_seconds: choice.end_time_seconds,
    pause_for_choice: choice.pause_for_choice,
  };
}

interface SceneEditorProps {
  story: StoryListItem | null;
  scenes: SceneListItem[];
  activeSceneIndex: number;
  realm: RealmId;
  coverboxUrl?: string | null;
  storySceneMap?: Record<string, SceneMapEntry> | null;
  user?: UserProfile | null;
  onNavigate: (index: number) => void;
  onClose: () => void;
  onSceneUpdated?: (scene: SceneListItem) => void;
  onScenesChanged?: (scenes: SceneListItem[]) => void;
  onStoryStructureChange?: () => void;
  onGeneratingChange?: (sceneId: string, isGenerating: boolean) => void;
}

export function SceneEditor({
  story,
  scenes,
  activeSceneIndex,
  realm,
  coverboxUrl = null,
  storySceneMap = null,
  user = null,
  onNavigate,
  onClose,
  onSceneUpdated,
  onScenesChanged,
  onStoryStructureChange,
  onGeneratingChange,
}: SceneEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorAutoPlayStartedRef = useRef(false);
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [insertMenuOpen, setInsertMenuOpen] = useState(false);
  const [altMediaUrl, setAltMediaUrl] = useState<string | null>(null);
  const [loadingAltMedia, setLoadingAltMedia] = useState(false);
  const [scenePrimaryMediaUrl, setScenePrimaryMediaUrl] = useState<string | null>(null);
  const [sceneChoices, setSceneChoices] = useState<SceneChoice[]>([]);
  const [syntheticExitDraft, setSyntheticExitDraft] = useState<SceneChoice | null>(null);
  const [gotoSceneId, setGotoSceneId] = useState<string | null>(null);
  const [sceneDurationSec, setSceneDurationSec] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [createSceneFlow, setCreateSceneFlow] = useState<CreateSceneFlow>(null);
  const [createTitleDraft, setCreateTitleDraft] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [chuziAiScenePanel, setChuziAiScenePanel] = useState<null | { mode: AiSceneMode }>(null);
  const [generatorMode, setGeneratorMode] = useState<"idle" | "entry" | "generating">("idle");
  const [sceneMutationError, setSceneMutationError] = useState<string | null>(null);

  useCloseAllModalsListener(
    useCallback(() => {
      setChuziAiScenePanel(null);
      setGeneratorMode("idle");
      setCreateSceneFlow(null);
    }, []),
  );

  const [deleteState, setDeleteState] = useState<DeleteState>("idle");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [authoringMode, setAuthoringMode] = useState<SceneMediaMode>("text");
  const [textLines, setTextLines] = useState<TextLine[]>([createDefaultLine()]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [textFontFamily, setTextFontFamily] = useState("inherit");
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [textBackgroundColor, setTextBackgroundColor] = useState(DEFAULT_TEXT_BACKGROUND);
  const [textFontSize, setTextFontSize] = useState(DEFAULT_SCENE_FONT_SIZE);

  // Shared playback state (used by both Generator and Effects tabs)
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadMs, setPlayheadMs] = useState(0);
  const playbackAnimRef = useRef<number>(0);
  const playbackLastTimeRef = useRef<number>(0);
  const previewAreaRef = useRef<HTMLDivElement | null>(null);
  const [selectedEffectId, setSelectedEffectId] = useState<string | null>(null);
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const choiceActionIdsRef = useRef<Record<string, string>>({});
  const sceneChoicesRef = useRef<SceneChoice[]>([]);
  const pendingScenePatchRef = useRef<Record<string, unknown>>({});
  const scenePatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scenePatchSaveSeqRef = useRef(0);
  const pendingChoicePatchesRef = useRef<
    Map<string, { choice: SceneChoice; refreshGraph: boolean }>
  >(new Map());
  const choicePatchTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const activeScene = scenes[activeSceneIndex] ?? null;
  const isEmptyStory = isUninitializedStory(scenes);
  const showGeneratorEntry = generatorMode === "entry";
  const showStoryGenerator = generatorMode === "generating";
  const inGeneratorFlow = showGeneratorEntry || showStoryGenerator;

  const canGoUp = activeSceneIndex > 0;
  const canGoDown = activeSceneIndex < scenes.length - 1;
  const canDeleteScene = Boolean(activeScene && story);
  const textHtml = textLines.map((l) => l.html).join("");
  const hasTextContent = textHtml.replace(/<[^>]*>/g, "").trim().length > 0;
  const hasAnyMediaContent = hasTextContent;

  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const accentDim = isWilds ? "rgba(120,200,120,0.3)" : "rgba(126,184,255,0.3)";
  const borderColor = isWilds ? "rgba(90,180,90,0.4)" : "rgba(126,184,255,0.35)";

  const navArrowButton = (
    direction: "prev" | "next",
    enabled: boolean,
    onClick: () => void,
    label: string,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      style={{
        background: "none",
        border: "none",
        cursor: enabled ? "pointer" : "default",
        opacity: enabled ? 1 : 0.08,
        padding: 4,
        flexShrink: 0,
        transition: "opacity 180ms ease",
      }}
      title={label}
      aria-label={label}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        {direction === "prev" ? (
          <path
            d="M10 3L5 8l5 5"
            stroke={enabled ? accent : "rgba(232,240,255,0.12)"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M6 3l5 5-5 5"
            stroke={enabled ? accent : "rgba(232,240,255,0.12)"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </button>
  );

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (editorAutoPlayStartedRef.current || inGeneratorFlow || !activeScene) return;
    editorAutoPlayStartedRef.current = true;
    const timer = setTimeout(() => {
      setPlayheadMs(0);
      setIsPlaying(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [activeScene?.id, inGeneratorFlow]);

  useEffect(() => {
    return () => {
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      if (scenePatchTimerRef.current) clearTimeout(scenePatchTimerRef.current);
      choicePatchTimersRef.current.forEach((timer) => clearTimeout(timer));
      choicePatchTimersRef.current.clear();
    };
  }, []);


  // Fetch scene actions, map entry, and media when scene changes
  useEffect(() => {
    setAltMediaUrl(null);
    setScenePrimaryMediaUrl(null);
    if (!activeScene) {
      setSceneChoices([]);
      return;
    }

    if (activeScene.alt_media_id) {
      setLoadingAltMedia(true);
      api.media
        .sourceUrl(activeScene.alt_media_id)
        .then((res) => setAltMediaUrl(res.url))
        .catch(() => setAltMediaUrl(null))
        .finally(() => setLoadingAltMedia(false));
    }

    let cancelled = false;
    const mapEntry = storySceneMap?.[activeScene.id] ?? null;

    const applyResolvedChoices = (
      actions: Awaited<ReturnType<typeof api.sceneActions.index>> | null,
      entry: SceneMapEntry | null,
      actionMap: Record<string, string> = {},
    ) => {
      if (actions) {
        const fromActions = choicesFromSceneActions(actions);
        if (fromActions.length > 0) {
          setSceneChoices(fromActions);
          return;
        }
        if (entry) {
          const choiceActions = actions.filter((action) => action.type === "choice");
          const remapped = resolveSceneChoices(entry).map((choice) => {
            const linked = choiceActions.find((action) => action.scene_choice_id === choice.id);
            return {
              ...choice,
              id: linked?.id ?? actionMap[choice.id] ?? choice.id,
            };
          });
          if (remapped.length > 0) {
            setSceneChoices(remapped);
            return;
          }
        }
      }
      if (entry) {
        const resolved = resolveSceneChoices(entry).map((choice) => ({
          ...choice,
          id: actionMap[choice.id] ?? choice.id,
        }));
        setSceneChoices(resolved);
      }
    };

    const loadSceneContext = (async () => {
      let resolvedEntry = mapEntry;
      if (!resolvedEntry && story) {
        try {
          const map = await api.watch.sceneMap(story.id);
          if (cancelled) return;
          resolvedEntry = map.scene_map[activeScene.id] ?? null;
        } catch { /* fall through */ }
      }

      if (cancelled) return;
      if (resolvedEntry) {
        setGotoSceneId(resolvedEntry.goto_scene_id);
        if (resolvedEntry.media_url) setScenePrimaryMediaUrl(resolvedEntry.media_url);
        applyResolvedChoices(null, resolvedEntry);
      }

      if (!resolvedEntry?.media_url && activeScene.media_id) {
        try {
          const res = await api.media.sourceUrl(activeScene.media_id);
          if (!cancelled) setScenePrimaryMediaUrl(res.url);
        } catch { /* no primary media */ }
      }

      try {
        const actions = await api.sceneActions.index({ sceneId: activeScene.id });
        if (cancelled) return;

        const actionMap: Record<string, string> = {};
        for (const action of actions) {
          if (action.scene_choice_id) {
            actionMap[action.scene_choice_id] = action.id;
          } else if (action.type === "choice") {
            actionMap[action.id] = action.id;
          }
        }
        choiceActionIdsRef.current = actionMap;
        applyResolvedChoices(actions, resolvedEntry, actionMap);
      } catch {
        if (!cancelled && resolvedEntry) {
          applyResolvedChoices(null, resolvedEntry, choiceActionIdsRef.current);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [activeScene?.id, activeScene?.alt_media_id, activeScene?.media_id, story?.id, storySceneMap]);

  useEffect(() => {
    if (!activeScene) return;
    const nextMode = activeScene.media_mode ?? "text";
    setAuthoringMode(nextMode);
    const loadedLines = activeScene.text_content?.lines;
    if (loadedLines && loadedLines.length > 0) {
      const normalized = normalizeLoadedTextLines(loadedLines);
      setTextLines(normalized);
      setSelectedLineId(normalized[0].id);
    } else {
      const cachedRaw = localStorage.getItem(`chuzi:textLines:${activeScene.id}`);
      const cached: TextLine[] | null = cachedRaw ? JSON.parse(cachedRaw) : null;
      if (cached && cached.length > 0) {
        const normalized = normalizeLoadedTextLines(cached);
        setTextLines(normalized);
        setSelectedLineId(normalized[0].id);
      } else {
        const defaultLine = createDefaultLine();
        if (activeScene.text_content?.html) {
          defaultLine.html = activeScene.text_content.html;
        }
        setTextLines([defaultLine]);
        setSelectedLineId(defaultLine.id);
      }
    }
    setTextFontFamily(resolveSceneTextFont(activeScene.text_style?.font_family).id);
    setTextColor(activeScene.text_style?.text_color ?? DEFAULT_TEXT_COLOR);
    setTextBackgroundColor(activeScene.text_style?.background_color ?? DEFAULT_TEXT_BACKGROUND);
    setTextFontSize(normalizeSceneFontSize(activeScene.text_style?.font_size));
    setSceneDurationSec(activeScene.choice_end_time_seconds ?? null);
    setSyntheticExitDraft(null);
  }, [activeScene?.id, activeScene?.updated_at]);

  useEffect(() => {
    if (!activeScene || activeScene.is_end) return;
    if (activeScene.title === "Start") {
      const newTitle = activeScene.is_title
        ? (story?.title || t(realm, "untitled_scene"))
        : t(realm, "untitled_scene");
      void api.scenes.update(activeScene.id, { title: newTitle }).then((updated) => {
        onSceneUpdated?.(updated);
      }).catch(() => {});
    }
  }, [activeScene?.id]);

  const showDefaultSlate = activeScene
    ? sceneShowsDefaultSlateWithLines(activeScene, textLines)
    : false;

  const totalDurationMs = computeTotalDuration(textLines);
  const activeSceneMapEntry = useMemo(
    () => (activeScene && storySceneMap ? storySceneMap[activeScene.id] ?? null : null),
    [activeScene, storySceneMap],
  );
  const editorPreviewChoices = useMemo(() => {
    if (!activeScene) return [];
    const resolved = resolveEditorPreviewChoices(
      activeScene,
      scenes,
      activeSceneMapEntry,
      sceneChoices,
    );
    if (!syntheticExitDraft) return resolved;
    return resolved.map((choice) =>
      choice.id === VIEWER_EXIT_CHOICE_ID ? syntheticExitDraft : choice,
    );
  }, [activeScene, scenes, activeSceneMapEntry, sceneChoices, syntheticExitDraft]);
  const effectivePreviewDurationMs = useMemo(() => {
    if (showDefaultSlate) return DEFAULT_SLATE_DURATION_MS;
    return computeEffectiveSceneDuration(totalDurationMs, editorPreviewChoices, sceneDurationSec);
  }, [showDefaultSlate, totalDurationMs, editorPreviewChoices, sceneDurationSec]);
  const choiceFallbackAppearMs = Math.max(0, effectivePreviewDurationMs - 3000);

  sceneChoicesRef.current = editorPreviewChoices;

  const scrubPreviewToMs = useCallback((ms: number) => {
    setPlayheadMs(Math.max(0, Math.min(ms, effectivePreviewDurationMs)));
    setIsPlaying(false);
  }, [effectivePreviewDurationMs]);

  const soundLines = useMemo(
    () => textLines.filter((l) => l.type === "sound" && l.media_id),
    [textLines],
  );
  const imageLines = useMemo(
    () => textLines.filter((l) => l.type === "image" && l.media_id),
    [textLines],
  );
  const [soundUrls, setSoundUrls] = useState<Record<string, string>>({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const resolvedMediaUrlRef = useRef<Set<string>>(new Set());
  const previewMediaUrls = useMemo(
    () => ({ ...imageUrls, ...soundUrls }),
    [imageUrls, soundUrls],
  );
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const activeSoundsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const linesWithMedia = [...soundLines, ...imageLines];
    if (linesWithMedia.length === 0) return;
    let cancelled = false;
    const pendingSounds: Record<string, string> = {};
    const pendingImages: Record<string, string> = {};
    Promise.all(
      linesWithMedia.map(async (line) => {
        const mid = line.media_id!;
        if (resolvedMediaUrlRef.current.has(mid)) return;
        resolvedMediaUrlRef.current.add(mid);
        try {
          const res = await api.media.sourceUrl(mid);
          if (cancelled) return;
          if (line.type === "image") pendingImages[mid] = res.url;
          else pendingSounds[mid] = res.url;
        } catch {
          resolvedMediaUrlRef.current.delete(mid);
        }
      }),
    ).then(() => {
      if (cancelled) return;
      if (Object.keys(pendingSounds).length > 0) {
        setSoundUrls((prev) => ({ ...prev, ...pendingSounds }));
      }
      if (Object.keys(pendingImages).length > 0) {
        setImageUrls((prev) => ({ ...prev, ...pendingImages }));
      }
    });
    return () => { cancelled = true; };
  }, [soundLines, imageLines]);

  useEffect(() => {
    const active = activeSoundsRef.current;
    soundLines.forEach((line) => {
      const url = soundUrls[line.media_id!];
      if (!url) return;

      const start = line.appear_at_ms;
      const fadeInEnd = start + line.fade_in_ms;
      const holdEnd = fadeInEnd + line.duration_ms;
      const fadeOutEnd = holdEnd + line.fade_out_ms;
      const shouldPlay = isPlaying && playheadMs >= start && playheadMs < fadeOutEnd;

      let audio = audioRefs.current[line.id];

      if (shouldPlay) {
        if (!audio) {
          audio = new Audio(url);
          audioRefs.current[line.id] = audio;
        }
        let vol = 1;
        if (playheadMs < fadeInEnd && line.fade_in_ms > 0)
          vol = (playheadMs - start) / line.fade_in_ms;
        else if (playheadMs >= holdEnd && line.fade_out_ms > 0)
          vol = 1 - (playheadMs - holdEnd) / line.fade_out_ms;
        audio.volume = Math.max(0, Math.min(1, vol));

        if (!active.has(line.id)) {
          const offset = (playheadMs - start) / 1000;
          audio.currentTime = Math.min(offset, audio.duration || Infinity);
          audio.play().catch(() => {});
          active.add(line.id);
        }
      } else if (audio && active.has(line.id)) {
        audio.pause();
        active.delete(line.id);
      }
    });

    if (!isPlaying) {
      Object.entries(audioRefs.current).forEach(([id, audio]) => {
        audio.pause();
        active.delete(id);
      });
    }
  }, [playheadMs, isPlaying, soundLines, soundUrls]);

  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((a) => { a.pause(); a.src = ""; });
      audioRefs.current = {};
      activeSoundsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setSelectedEffectId(null);
    setSelectedChoiceId(null);
    setSelectedLineId(null);
  }, [activeScene?.id]);

  const resolveChoiceActionId = useCallback(
    (choiceId: string) => choiceActionIdsRef.current[choiceId] ?? choiceId,
    [],
  );

  const flushScenePatchQueue = useCallback(async () => {
    if (!activeScene) return;
    const patch = pendingScenePatchRef.current;
    if (Object.keys(patch).length === 0) return;
    pendingScenePatchRef.current = {};
    const seq = ++scenePatchSaveSeqRef.current;
    try {
      const updated = await api.scenes.update(activeScene.id, patch);
      if (seq === scenePatchSaveSeqRef.current) {
        onSceneUpdated?.(updated);
      }
    } catch {
      if (seq === scenePatchSaveSeqRef.current && activeScene) {
        onSceneUpdated?.(activeScene);
      }
    }
  }, [activeScene, onSceneUpdated]);

  const scheduleSceneAutosave = useCallback((immediate = false) => {
    if (scenePatchTimerRef.current) {
      clearTimeout(scenePatchTimerRef.current);
      scenePatchTimerRef.current = null;
    }
    if (immediate) {
      void flushScenePatchQueue();
      return;
    }
    scenePatchTimerRef.current = setTimeout(() => {
      scenePatchTimerRef.current = null;
      void flushScenePatchQueue();
    }, SCENE_AUTOSAVE_DEBOUNCE_MS);
  }, [flushScenePatchQueue]);

  const mergeScenePatch = useCallback((
    patch: Record<string, unknown>,
    schedule = true,
  ) => {
    pendingScenePatchRef.current = { ...pendingScenePatchRef.current, ...patch };
    if (schedule) scheduleSceneAutosave();
  }, [scheduleSceneAutosave]);

  const applyTextAuthoringToPendingPatch = useCallback((
    payload: TextAuthoringPayload,
    schedule = true,
  ) => {
    try {
      localStorage.setItem(`chuzi:textLines:${payload.sceneId}`, JSON.stringify(payload.lines));
    } catch { /* quota exceeded — non-critical */ }
    mergeScenePatch(textAuthoringPayloadToScenePatch(payload), schedule);
  }, [mergeScenePatch]);

  const flushPendingChoicePatch = useCallback((choiceId: string): Promise<boolean> => {
    const pendingTimer = choicePatchTimersRef.current.get(choiceId);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      choicePatchTimersRef.current.delete(choiceId);
    }
    const pending = pendingChoicePatchesRef.current.get(choiceId);
    if (!pending) return Promise.resolve(false);
    pendingChoicePatchesRef.current.delete(choiceId);
    return api.sceneActions
      .update(resolveChoiceActionId(pending.choice.id), {
        properties: choiceToActionProperties(pending.choice),
      })
      .then(() => pending.refreshGraph)
      .catch(() => false);
  }, [resolveChoiceActionId]);

  const flushAllPendingChoicePatches = useCallback(async (): Promise<boolean> => {
    choicePatchTimersRef.current.forEach((timer) => clearTimeout(timer));
    choicePatchTimersRef.current.clear();
    const choiceIds = [...pendingChoicePatchesRef.current.keys()];
    if (choiceIds.length === 0) return false;
    const results = await Promise.all(
      choiceIds.map((choiceId) => flushPendingChoicePatch(choiceId)),
    );
    return results.some(Boolean);
  }, [flushPendingChoicePatch]);

  const persistChoiceProperties = useCallback((
    choice: SceneChoice,
    options?: { refreshGraph?: boolean; immediate?: boolean },
  ) => {
    if (isViewerExitSuppressionMarker(choice)) return;
    if (isViewerExitChoice(choice) && choice.id === VIEWER_EXIT_CHOICE_ID) return;
    const existing = pendingChoicePatchesRef.current.get(choice.id);
    pendingChoicePatchesRef.current.set(choice.id, {
      choice,
      refreshGraph: Boolean(options?.refreshGraph || existing?.refreshGraph),
    });

    const pendingTimer = choicePatchTimersRef.current.get(choice.id);
    if (pendingTimer) clearTimeout(pendingTimer);

    if (options?.immediate) {
      void flushPendingChoicePatch(choice.id).then((needsGraphRefresh) => {
        if (needsGraphRefresh) onStoryStructureChange?.();
      });
      return;
    }

    choicePatchTimersRef.current.set(
      choice.id,
      setTimeout(() => {
        void flushPendingChoicePatch(choice.id).then((needsGraphRefresh) => {
          if (needsGraphRefresh) onStoryStructureChange?.();
        });
      }, CHOICE_AUTOSAVE_DEBOUNCE_MS),
    );
  }, [flushPendingChoicePatch, onStoryStructureChange]);

  const updateChoiceLayout = useCallback((
    choiceId: string,
    patch: Partial<Pick<SceneChoice, "x" | "y" | "w" | "h" | "arrow_rotation" | "arrow_scale">>,
  ) => {
    if (choiceId === VIEWER_EXIT_CHOICE_ID) {
      setSyntheticExitDraft((prev) => {
        const base = prev ?? sceneChoicesRef.current.find((c) => c.id === VIEWER_EXIT_CHOICE_ID);
        if (!base) return prev;
        return { ...base, ...patch };
      });
      return;
    }
    setSceneChoices((prev) => prev.map((c) => (c.id === choiceId ? { ...c, ...patch } : c)));
  }, []);

  const commitChoiceLayout = useCallback(async (choiceId: string) => {
    let choice = sceneChoicesRef.current.find((c) => c.id === choiceId);
    if (!choice || isViewerExitSuppressionMarker(choice)) return;
    if (isViewerExitChoice(choice) && choice.id === VIEWER_EXIT_CHOICE_ID) {
      if (!activeScene) return;
      try {
        const action = await api.sceneActions.create({
          scene_id: activeScene.id,
          name: t(realm, "editor_exit_choice_label", "Go To The End"),
          type: "choice",
          order: sceneChoices.length,
          properties: choiceToActionProperties(choice),
        });
        choiceActionIdsRef.current[action.id] = action.id;
        const persisted = { ...choice, id: action.id };
        setSceneChoices((prev) => [
          ...prev.filter((c) => !isViewerExitSuppressionMarker(c)),
          persisted,
        ]);
        setSyntheticExitDraft(null);
        if (selectedChoiceId === VIEWER_EXIT_CHOICE_ID) {
          setSelectedChoiceId(persisted.id);
        }
        persistChoiceProperties(persisted, { immediate: true });
        if (story) persistStoryChoiceArrowDefaults(story.id, persisted);
        onStoryStructureChange?.();
      } catch {
        // silent
      }
      return;
    }
    persistChoiceProperties(choice, { immediate: true });
    if (story) persistStoryChoiceArrowDefaults(story.id, choice);
  }, [
    activeScene,
    sceneChoices.length,
    realm,
    persistChoiceProperties,
    story,
    selectedChoiceId,
    onStoryStructureChange,
  ]);

  const handleChoiceArrowDrag = useCallback((
    choiceId: string,
    rect: { x: number; y: number; w: number; h: number },
    persist = false,
  ) => {
    updateChoiceLayout(choiceId, rect);
    if (persist) commitChoiceLayout(choiceId);
  }, [updateChoiceLayout, commitChoiceLayout]);

  const handleChoiceArrowResize = useCallback((
    choiceId: string,
    scale: number,
    rotation: number,
    persist = false,
  ) => {
    updateChoiceLayout(choiceId, { arrow_scale: scale, arrow_rotation: rotation });
    if (persist) commitChoiceLayout(choiceId);
  }, [updateChoiceLayout, commitChoiceLayout]);

  const handleSelectChoice = useCallback((choiceId: string) => {
    setSelectedChoiceId(choiceId);
    const choice = sceneChoicesRef.current.find((c) => c.id === choiceId);
    if (choice) {
      scrubPreviewToMs(
        previewMsWhenVisible(resolveChoiceTiming(choice, choiceFallbackAppearMs)),
      );
    }
  }, [choiceFallbackAppearMs, scrubPreviewToMs]);

  const handleChoiceArrowRotateStart = useCallback((
    e: React.MouseEvent,
    choice: SceneChoice,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!previewAreaRef.current) return;
    handleSelectChoice(choice.id);

    const rect = previewAreaRef.current.getBoundingClientRect();
    const centerX = rect.left + ((choice.x + choice.w / 2) / 100) * rect.width;
    const centerY = rect.top + ((choice.y + choice.h / 2) / 100) * rect.height;

    const handleMove = (ev: MouseEvent) => {
      const dx = ev.clientX - centerX;
      const dy = ev.clientY - centerY;
      const rotation = ((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
      handleChoiceArrowResize(choice.id, choice.arrow_scale, Math.round(rotation));
    };
    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      commitChoiceLayout(choice.id);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }, [handleChoiceArrowResize, handleSelectChoice, commitChoiceLayout]);

  useEffect(() => {
    if (!isPlaying) return;
    playbackLastTimeRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      const delta = now - playbackLastTimeRef.current;
      playbackLastTimeRef.current = now;
      setPlayheadMs((prev) => {
        const next = prev + delta;
        if (next >= effectivePreviewDurationMs) {
          setIsPlaying(false);
          return effectivePreviewDurationMs;
        }
        return next;
      });
      playbackAnimRef.current = requestAnimationFrame(tick);
    };
    playbackAnimRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(playbackAnimRef.current);
  }, [isPlaying, effectivePreviewDurationMs]);

  const handleSaveTitle = useCallback(async () => {
    if (!activeScene || !titleDraft.trim()) return;
    try {
      const updated = await api.scenes.update(activeScene.id, {
        title: titleDraft.trim(),
      });
      onSceneUpdated?.(updated);
    } catch {
      // handled silently
    } finally {
      setEditingTitle(false);
    }
  }, [activeScene, titleDraft, onSceneUpdated]);

  const handleColorChange = useCallback(
    (color: string | null) => {
      if (!activeScene) return;
      const currentColor = activeScene.color ?? null;
      if (color === currentColor) return;
      onSceneUpdated?.({ ...activeScene, color });
      mergeScenePatch({ color });
    },
    [activeScene, mergeScenePatch, onSceneUpdated],
  );

  const buildTextAuthoringPayload = useCallback(
    (next: {
      mediaMode?: SceneMediaMode;
      lines?: TextLine[];
      fontFamily?: string;
      textColor?: string;
      backgroundColor?: string;
      fontSize?: number;
    } = {}): TextAuthoringPayload | null => {
      if (!activeScene) return null;
      return {
        sceneId: activeScene.id,
        mediaMode: next.mediaMode ?? authoringMode,
        lines: next.lines ?? textLines,
        fontFamily: next.fontFamily ?? textFontFamily,
        textColor: next.textColor ?? textColor,
        backgroundColor: next.backgroundColor ?? textBackgroundColor,
        fontSize: next.fontSize ?? textFontSize,
      };
    },
    [activeScene, authoringMode, textLines, textFontFamily, textColor, textBackgroundColor, textFontSize],
  );

  const flushPendingSceneAutosave = useCallback(async () => {
    if (scenePatchTimerRef.current) {
      clearTimeout(scenePatchTimerRef.current);
      scenePatchTimerRef.current = null;
    }
    const payload = buildTextAuthoringPayload();
    if (payload) applyTextAuthoringToPendingPatch(payload, false);
    const choiceGraphRefresh = await flushAllPendingChoicePatches();
    await flushScenePatchQueue();
    if (choiceGraphRefresh) onStoryStructureChange?.();
  }, [
    applyTextAuthoringToPendingPatch,
    buildTextAuthoringPayload,
    flushAllPendingChoicePatches,
    flushScenePatchQueue,
    onStoryStructureChange,
  ]);

  const flushPendingSceneAutosaveRef = useRef(flushPendingSceneAutosave);
  flushPendingSceneAutosaveRef.current = flushPendingSceneAutosave;

  const persistTextAuthoring = useCallback(
    (next: {
      mediaMode?: SceneMediaMode;
      lines?: TextLine[];
      fontFamily?: string;
      textColor?: string;
      backgroundColor?: string;
      fontSize?: number;
    }) => {
      const payload = buildTextAuthoringPayload(next);
      if (!payload) return;
      applyTextAuthoringToPendingPatch(payload);
    },
    [applyTextAuthoringToPendingPatch, buildTextAuthoringPayload],
  );

  useEffect(() => {
    pendingScenePatchRef.current = {};
    pendingChoicePatchesRef.current.clear();
    choicePatchTimersRef.current.forEach((timer) => clearTimeout(timer));
    choicePatchTimersRef.current.clear();
    return () => {
      void flushPendingSceneAutosaveRef.current();
    };
  }, [activeScene?.id]);

  const navigateToScene = useCallback(async (index: number) => {
    await flushPendingSceneAutosave();
    onNavigate(index);
  }, [flushPendingSceneAutosave, onNavigate]);

  const handleClose = useCallback(async () => {
    await flushPendingSceneAutosave();
    onClose();
  }, [flushPendingSceneAutosave, onClose]);

  const choicePreviewOverlay = useMemo(() => {
    if (editorPreviewChoices.length === 0) return null;
    const isChoicesTab = selectedChoiceId !== null;
    const actionColor = isWilds ? "rgba(180,230,120,0.9)" : "rgba(245,196,85,0.9)";
    const actionBorder = isWilds ? "rgba(160,210,100,0.5)" : "rgba(245,186,65,0.5)";
    const actionBg = isWilds ? "rgba(160,210,100,0.12)" : "rgba(245,186,65,0.12)";

    return (
      <>
        {editorPreviewChoices.map((choice) => {
          const timing = resolveChoiceTiming(choice, choiceFallbackAppearMs);
          const opacity = computeChoiceOpacity(timing, playheadMs);
          if (opacity <= 0) return null;
          const isExit = isViewerExitChoice(choice);

          if (isExit && !isChoicesTab) {
            return (
              <div
                key={`arrow-${choice.id}`}
                role="button"
                tabIndex={0}
                style={{
                  position: "absolute",
                  left: `${choice.x}%`,
                  top: `${choice.y}%`,
                  width: `${choice.w}%`,
                  height: `${choice.h}%`,
                  cursor: "pointer",
                  zIndex: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "all",
                  borderRadius: 4,
                  opacity,
                  transition: "opacity 0.05s linear",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateSceneFlow("downstream");
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  setCreateSceneFlow("downstream");
                }}
              >
                <ChoiceFlashArrow
                  color={actionColor}
                  rotation={choice.arrow_rotation}
                  scale={choice.arrow_scale}
                />
                <span
                  style={{
                    position: "absolute",
                    bottom: -18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: actionColor,
                    textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: `1px solid ${actionBorder}`,
                    background: actionBg,
                  }}
                >
                  {t(realm, "editor_create_scene_ahead", "Create new scene")}
                </span>
              </div>
            );
          }

          return (
            <div
              key={`arrow-${choice.id}`}
              role={isChoicesTab ? undefined : "button"}
              tabIndex={isChoicesTab ? undefined : 0}
              style={{
                position: "absolute",
                left: `${choice.x}%`,
                top: `${choice.y}%`,
                width: `${choice.w}%`,
                height: `${choice.h}%`,
                cursor: isChoicesTab ? "move" : "pointer",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "all",
                outline: selectedChoiceId === choice.id && isChoicesTab
                  ? `2px dashed ${accent}`
                  : "none",
                borderRadius: 4,
                opacity,
                transition: "opacity 0.05s linear",
              }}
              onClick={(e) => {
                if (isChoicesTab) return;
                e.stopPropagation();
                if (isExit) return;
                const targetIndex = scenes.findIndex((s) => s.id === choice.target_scene_id);
                if (targetIndex >= 0) void navigateToScene(targetIndex);
              }}
              onKeyDown={(e) => {
                if (isChoicesTab || (e.key !== "Enter" && e.key !== " ")) return;
                e.preventDefault();
                if (isExit) return;
                const targetIndex = scenes.findIndex((s) => s.id === choice.target_scene_id);
                if (targetIndex >= 0) void navigateToScene(targetIndex);
              }}
              onMouseDown={isChoicesTab ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectChoice(choice.id);
                const handleMove = (ev: MouseEvent) => {
                  if (!previewAreaRef.current) return;
                  const rect = previewAreaRef.current.getBoundingClientRect();
                  const pctX = ((ev.clientX - rect.left) / rect.width) * 100;
                  const pctY = ((ev.clientY - rect.top) / rect.height) * 100;
                  handleChoiceArrowDrag(choice.id, {
                    x: Math.max(0, Math.min(100 - choice.w, pctX - choice.w / 2)),
                    y: Math.max(0, Math.min(100 - choice.h, pctY - choice.h / 2)),
                    w: choice.w,
                    h: choice.h,
                  });
                };
                const handleUp = () => {
                  window.removeEventListener("mousemove", handleMove);
                  window.removeEventListener("mouseup", handleUp);
                  commitChoiceLayout(choice.id);
                };
                window.addEventListener("mousemove", handleMove);
                window.addEventListener("mouseup", handleUp);
              } : undefined}
            >
              <ChoiceFlashArrow
                color={accent}
                rotation={choice.arrow_rotation}
                scale={choice.arrow_scale}
              />
              {choice.label ? (
                <span
                  style={{
                    position: "absolute",
                    bottom: -18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(232,240,255,0.85)",
                    textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >
                  {t(realm, choice.label, choice.label)}
                </span>
              ) : null}
              {selectedChoiceId === choice.id && isChoicesTab ? (
                <div
                  title={t(realm, "editor_arrow_rotation", "Rotation")}
                  onMouseDown={(e) => handleChoiceArrowRotateStart(e, choice)}
                  style={{
                    position: "absolute",
                    top: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: "rgba(4,7,13,0.92)",
                    border: `2px solid ${accent}`,
                    boxShadow: `0 0 10px ${accent}`,
                    cursor: "grab",
                    zIndex: 12,
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </>
    );
  }, [
    editorPreviewChoices,
    playheadMs,
    choiceFallbackAppearMs,
    selectedChoiceId,
    accent,
    isWilds,
    realm,
    scenes,
    navigateToScene,
    handleChoiceArrowDrag,
    handleChoiceArrowRotateStart,
    handleSelectChoice,
    commitChoiceLayout,
  ]);

  const handleSceneDurationChange = useCallback(
    (sec: number | null) => {
      if (!activeScene) return;
      setSceneDurationSec(sec);
      mergeScenePatch({ choice_end_time_seconds: sec });
    },
    [activeScene, mergeScenePatch],
  );

  const handleAuthoringModeChange = useCallback(
    (mode: SceneMediaMode) => {
      setAuthoringMode(mode);
      void persistTextAuthoring({ mediaMode: mode });
    },
    [persistTextAuthoring],
  );

  const handleLinesChange = useCallback(
    (lines: TextLine[]) => {
      setTextLines(lines);
      void persistTextAuthoring({ lines });
    },
    [persistTextAuthoring],
  );

  const handleTextStyleChange = useCallback(
    (next: {
      fontFamily?: string;
      textColor?: string;
      backgroundColor?: string;
      fontSize?: number;
    }) => {
      if (next.fontFamily !== undefined) setTextFontFamily(next.fontFamily);
      if (next.textColor !== undefined) setTextColor(next.textColor);
      if (next.backgroundColor !== undefined) setTextBackgroundColor(next.backgroundColor);
      if (next.fontSize !== undefined) setTextFontSize(normalizeSceneFontSize(next.fontSize));
      persistTextAuthoring(next);
    },
    [persistTextAuthoring],
  );

  const handlePreviewLinePositionChange = useCallback((id: string, x: number, y: number) => {
    handleLinesChange(textLines.map((l) =>
      l.id === id ? { ...l, position_x: x, position_y: y } : l,
    ));
  }, [textLines, handleLinesChange]);

  const handlePreviewEffectResize = useCallback((id: string, w: number, h: number) => {
    handleLinesChange(textLines.map((l) =>
      l.id === id ? { ...l, width_pct: w, height_pct: h } : l,
    ));
  }, [textLines, handleLinesChange]);

  const handleChuziAiSceneApplied = useCallback(async (sceneId: string) => {
    setChuziAiScenePanel(null);
    if (!story) return;
    try {
      const refreshed = await api.scenes.index(story.id);
      onScenesChanged?.(refreshed);
      onStoryStructureChange?.();
      const nextIndex = refreshed.findIndex((scene) => scene.id === sceneId);
      if (nextIndex >= 0) void navigateToScene(nextIndex);
    } catch {
      // silent
    }
  }, [story, onScenesChanged, onStoryStructureChange, navigateToScene]);


  const handleCreateScene = useCallback(async () => {
    if (!story) return;
    const trimmedTitle = createTitleDraft.trim();
    if (!trimmedTitle || createSubmitting) return;

    setCreateSubmitting(true);
    setSceneMutationError(null);
    try {
      const created = await api.scenes.create({
        story_id: story.id,
        title: trimmedTitle,
      });
      const refreshed = await api.scenes.index(story.id);
      onScenesChanged?.(refreshed);
      const nextIndex = refreshed.findIndex((scene) => scene.id === created.id);
      if (nextIndex >= 0) void navigateToScene(nextIndex);
      setCreateSceneFlow(null);
      setCreateTitleDraft("");
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setSceneMutationError(body?.error ?? body?.message ?? `${err.status}`);
      } else {
        setSceneMutationError(err instanceof Error ? err.message : "error");
      }
    } finally {
      setCreateSubmitting(false);
    }
  }, [
    story,
    createTitleDraft,
    createSubmitting,
    onScenesChanged,
    onNavigate,
  ]);

  const handleDeleteClick = useCallback(async () => {
    if (
      !activeScene ||
      deleteSubmitting ||
      !story
    ) {
      return;
    }

    if (deleteState === "idle") {
      setDeleteState("confirming");
      deleteTimerRef.current = setTimeout(() => {
        setDeleteState("idle");
        deleteTimerRef.current = null;
      }, 3000);
      return;
    }

    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }

    setDeleteSubmitting(true);
    setDeleteState("idle");
    setSceneMutationError(null);
    try {
      await api.scenes.destroy(activeScene.id);

      const refreshed = await api.scenes.index(story.id);
      onScenesChanged?.(refreshed);
      if (refreshed.length === 0) {
        onClose();
      } else {
        const nextIndex = Math.min(activeSceneIndex, refreshed.length - 1);
        void navigateToScene(nextIndex);
      }
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setSceneMutationError(body?.error ?? body?.message ?? `${err.status}`);
      } else {
        setSceneMutationError(err instanceof Error ? err.message : "error");
      }
    } finally {
      setDeleteSubmitting(false);
    }
  }, [
    activeScene,
    activeSceneIndex,
    deleteState,
    deleteSubmitting,
    story,
    onScenesChanged,
    onNavigate,
  ]);

  const tokens = getThemeTokens(realm);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="scene-editor-root"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 250,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "rgba(4, 7, 13, 0.75)",
        backdropFilter: "blur(4px)",
        animation: "editorFadeIn 0.3s ease-out",
        overflow: "hidden",
      }}
    >
      {/* Close button */}
      <button
        onClick={() => { void handleClose(); }}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "rgba(232,240,255,0.08)",
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "background 0.2s",
          zIndex: 2,
        }}
        title="Close editor (Escape)"
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(232,240,255,0.15)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(232,240,255,0.08)"; }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4l8 8M12 4l-8 8" stroke="rgba(232,240,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* Story title header */}
      {story ? (
        <div style={{ paddingTop: 16, fontSize: 12, color: "rgba(232,240,255,0.5)", letterSpacing: 1, textTransform: "uppercase" }}>
          {story.title}
        </div>
      ) : null}

      {sceneMutationError ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: "#ff9aa2",
            background: "rgba(255,154,162,0.08)",
            border: "1px solid rgba(255,154,162,0.35)",
            borderRadius: 6,
            padding: "6px 10px",
            maxWidth: 560,
            textAlign: "center",
          }}
        >
          {sceneMutationError}
        </div>
      ) : null}

      {/* Central editor pane */}
      <div
        style={{
          flex: 1,
          width: "90%",
          maxWidth: 1200,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: "0 24px",
        }}
      >
        {activeScene ? (
          <>
            {/* Sacrosanct preview viewer — fixed placement across all tabs */}
            <div
              style={{
                flex: inGeneratorFlow ? "1 1 0" : "1.4 1 0",
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                marginBottom: 6,
              }}
            >
              {showGeneratorEntry || showStoryGenerator ? (
                showStoryGenerator && story ? (
                  <StoryGenerator
                    realm={realm}
                    storyId={story.id}
                    storyTitle={story.title}
                    user={user}
                    onComplete={async (sid, firstSceneId) => {
                      setGeneratorMode("idle");
                      try {
                        const refreshed = await api.scenes.index(sid);
                        onScenesChanged?.(refreshed);
                        onStoryStructureChange?.();
                        if (firstSceneId) {
                          const idx = refreshed.findIndex((s) => s.id === firstSceneId);
                          if (idx >= 0) {
                            void navigateToScene(idx);
                            setTimeout(() => {
                              setPlayheadMs(0);
                              setIsPlaying(true);
                            }, 400);
                          }
                        }
                      } catch {
                        onStoryStructureChange?.();
                      }
                    }}
                    onCancel={() => setGeneratorMode("idle")}
                  />
                ) : (
                  <GeneratorEntry
                    realm={realm}
                    user={user}
                    onCreateUpload={() => setGeneratorMode("idle")}
                    onGenerate={() => setGeneratorMode("generating")}
                  />
                )
              ) : story ? (
                <SceneTransmissionStage
                  scene={activeScene}
                  story={story}
                  realm={realm}
                  textLines={textLines}
                  playheadMs={playheadMs}
                  isPlaying={isPlaying}
                  fontFamily={textFontFamily}
                  textColor={textColor}
                  backgroundColor={textBackgroundColor}
                  fontSize={textFontSize}
                  primaryMediaUrl={scenePrimaryMediaUrl}
                  mediaUrls={previewMediaUrls}
                  coverboxUrl={coverboxUrl}
                  effectiveDurationMs={effectivePreviewDurationMs}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onSeek={scrubPreviewToMs}
                  onLinePositionChange={handlePreviewLinePositionChange}
                  selectedEffectId={selectedEffectId ?? undefined}
                  onEffectResize={handlePreviewEffectResize}
                  overlay={choicePreviewOverlay}
                  onPreviewAreaRef={(el) => { previewAreaRef.current = el; }}
                  slateInteractive
                />
              ) : null}
            </div>

            {!inGeneratorFlow ? (
              <>
            {/* Scene title centered above tab bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 6, flexShrink: 0 }}>
              {navArrowButton(
                "prev",
                canGoUp,
                () => { if (canGoUp) void navigateToScene(activeSceneIndex - 1); },
                t(realm, "editor_prev_frame", "Previous scene"),
              )}
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 8,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: accent,
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: `1px solid ${accentDim}`,
                  background: isWilds ? "rgba(120,200,120,0.08)" : "rgba(126,184,255,0.08)",
                  boxShadow: `0 0 6px ${accentDim}`,
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                {t(realm, "editor_scene_type_label")}
              </span>
              {editingTitle ? (
                <div style={{ display: "flex", gap: 4, alignItems: "center", minWidth: 0 }}>
                  <input
                    autoFocus
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") void handleSaveTitle();
                      if (e.key === "Escape") setEditingTitle(false);
                    }}
                    style={{
                      minWidth: 120,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#e8f0ff",
                      background: "rgba(232,240,255,0.06)",
                      border: `1px solid ${borderColor}`,
                      borderRadius: 6,
                      padding: "3px 8px",
                      outline: "none",
                      fontFamily: "inherit",
                      textAlign: "center",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { void handleSaveTitle(); }}
                    style={{
                      background: accent,
                      border: "none",
                      borderRadius: 4,
                      padding: "3px 8px",
                      color: "#04070d",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      flexShrink: 0,
                      fontFamily: "inherit",
                    }}
                  >
                    {t(realm, "editor_save")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (activeScene) {
                      setTitleDraft(activeScene.title);
                      setEditingTitle(true);
                    }
                  }}
                  title={t(realm, "editor_scene_title_hint")}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#e8f0ff",
                    textAlign: "center",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    borderBottom: "1px dashed rgba(232,240,255,0.22)",
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeScene?.title}
                </button>
              )}
              {navArrowButton(
                "next",
                canGoDown,
                () => { if (canGoDown) void navigateToScene(activeSceneIndex + 1); },
                t(realm, "editor_next_frame", "Next scene"),
              )}
            </div>

            {/* Settings strip */}
            <EditorSettingsStrip
              scene={activeScene}
              realm={realm}
              tokens={tokens}
              canDeleteScene={canDeleteScene}
              deleteState={deleteState}
              deleteSubmitting={deleteSubmitting}
              onDeleteClick={handleDeleteClick}
              onColorChange={handleColorChange}
              sceneDurationSec={sceneDurationSec}
              contentDurationSec={totalDurationMs / 1000}
              onSceneDurationChange={handleSceneDurationChange}
              insertMenuOpen={insertMenuOpen}
              onInsertMenuToggle={() => setInsertMenuOpen((v) => !v)}
              onInsertBefore={() => { setInsertMenuOpen(false); setCreateSceneFlow("upstream"); }}
              onInsertAfter={() => { setInsertMenuOpen(false); setCreateSceneFlow("downstream"); }}
            />

            {(createSceneFlow === "upstream" || createSceneFlow === "downstream") && (
              <div style={{
                position: "fixed",
                inset: 0,
                zIndex: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(4,7,13,0.85)",
                backdropFilter: "blur(6px)",
                animation: "editorFadeIn 0.25s ease-out",
              }}>
                <div style={{
                  width: "90%",
                  maxWidth: 700,
                  aspectRatio: "16 / 9",
                  maxHeight: "60vh",
                  borderRadius: 14,
                  overflow: "hidden",
                  border: `1px solid ${isWilds ? "rgba(120,200,120,0.25)" : "rgba(126,184,255,0.25)"}`,
                  boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                  position: "relative",
                }}>
                  <button
                    type="button"
                    onClick={() => setCreateSceneFlow(null)}
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 2,
                      background: "rgba(232,240,255,0.08)",
                      border: `1px solid ${borderColor}`,
                      borderRadius: 8,
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="rgba(232,240,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  <GeneratorEntry
                    realm={realm}
                    user={user}
                    onCreateUpload={() => setCreateSceneFlow("blank")}
                    onGenerate={() => {
                      if (!story) return;
                      setCreateSceneFlow(null);
                      setSceneMutationError(null);
                      setChuziAiScenePanel({ mode: "create" });
                    }}
                  />
                </div>
              </div>
            )}

            {/* Unified workspace — node list + editor */}
            <div
              style={{
                flex: "0.9 1 0",
                minHeight: 140,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${isWilds ? "rgba(120,200,120,0.15)" : "rgba(126,184,255,0.15)"}`,
                borderRadius: 8,
                background: "rgba(6,10,18,0.85)",
              }}
            >
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: 8 }}>
                <UnifiedWorkspace
                  scene={activeScene}
                  story={story}
                  scenes={scenes}
                  realm={realm}
                  tokens={tokens}
                  user={user}
                  textLines={textLines}
                  selectedLineId={selectedLineId}
                  selectedEffectId={selectedEffectId}
                  selectedChoiceId={selectedChoiceId}
                  textFontFamily={textFontFamily}
                  textColor={textColor}
                  textBackgroundColor={textBackgroundColor}
                  textFontSize={textFontSize}
                  sceneChoices={editorPreviewChoices}
                  storedChoices={sceneChoices}
                  gotoSceneId={gotoSceneId}
                  totalDurationMs={totalDurationMs}
                  sceneDurationSec={sceneDurationSec}
                  choiceActionIdsRef={choiceActionIdsRef}
                  canRewriteWithAi={canDeleteScene}
                  onRewriteWithAi={() => setChuziAiScenePanel({ mode: "overwrite" })}
                  onLinesChange={handleLinesChange}
                  onSelectLine={setSelectedLineId}
                  onSelectEffect={setSelectedEffectId}
                  onSelectChoice={handleSelectChoice}
                  onSelectedChoiceIdChange={setSelectedChoiceId}
                  onTextStyleChange={handleTextStyleChange}
                  onScrubPreviewToMs={scrubPreviewToMs}
                  onGotoSceneIdChange={async (id) => {
                    setGotoSceneId(id);
                    if (activeScene) {
                      await api.scenes.update(activeScene.id, { goto_scene_id: id });
                      onStoryStructureChange?.();
                    }
                  }}
                  onChoicesChange={setSceneChoices}
                  onScenesChanged={onScenesChanged}
                  onStoryStructureChange={onStoryStructureChange}
                  persistChoiceProperties={persistChoiceProperties}
                  onChoiceArrowResize={handleChoiceArrowResize}
                  onShowGeneratorEntry={() => setGeneratorMode("entry")}
                />
              </div>
            </div>

              </>
            ) : null}
          </>
        ) : (
          <div style={{ color: "rgba(232,240,255,0.5)", fontSize: 14, textAlign: "center", paddingTop: 60 }}>
            No scene selected
          </div>
        )}
      </div>

      {chuziAiScenePanel && story ? (
        <ChuziAiWizard
          realm={realm}
          storyId={story.id}
          scenes={scenes}
          mode={chuziAiScenePanel.mode}
          targetScene={chuziAiScenePanel.mode === "overwrite" ? activeScene : null}
          insertAfterSceneId={
            chuziAiScenePanel.mode === "create" && activeScene
              ? activeScene.id
              : null
          }
          user={user}
          onApplied={(sceneId) => { void handleChuziAiSceneApplied(sceneId); }}
          onCancel={() => setChuziAiScenePanel(null)}
        />
      ) : null}


      <style>{`
        @keyframes editorFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes editorTabRimPulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        @keyframes editorTabBeam {
          0% { transform: translateX(-120%); opacity: 0; }
          35% { opacity: 0.85; }
          100% { transform: translateX(120%); opacity: 0; }
        }
        @keyframes chuzi-pulse-arrow {
          0%, 100% { transform: translateX(0); opacity: 0.7; }
          50% { transform: translateX(3px); opacity: 1; }
        }
        .scene-editor-root,
        .scene-editor-root * {
          scrollbar-width: none;
        }
        .scene-editor-root::-webkit-scrollbar,
        .scene-editor-root *::-webkit-scrollbar {
          display: none;
        }

        .editor-tab-dock {
          position: relative;
          flex: 1;
          min-width: 0;
          margin-bottom: 0;
          padding: 3px;
          border-radius: 14px;
          background:
            linear-gradient(165deg, rgba(18, 28, 52, 0.72) 0%, rgba(4, 7, 13, 0.94) 55%, rgba(2, 4, 9, 0.98) 100%);
          border: 1px solid var(--dock-border);
          box-shadow:
            0 0 0 1px rgba(0, 0, 0, 0.55) inset,
            0 14px 40px rgba(0, 0, 0, 0.55),
            0 0 48px var(--dock-ambient);
          backdrop-filter: blur(18px) saturate(140%);
          -webkit-backdrop-filter: blur(18px) saturate(140%);
          overflow: hidden;
        }
        .editor-tab-dock::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(
            90deg,
            transparent 0%,
            var(--dock-rim) 18%,
            var(--dock-accent) 50%,
            var(--dock-rim) 82%,
            transparent 100%
          );
          opacity: 0.65;
          height: 1px;
          top: 0;
          animation: editorTabRimPulse 4.5s ease-in-out infinite;
          pointer-events: none;
        }
        .editor-tab-dock::after {
          content: "";
          position: absolute;
          left: 8%;
          right: 8%;
          bottom: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--dock-accent-soft), transparent);
          opacity: 0.35;
          pointer-events: none;
        }
        .editor-tab-dock__track {
          display: flex;
          gap: 5px;
          padding: 6px;
          position: relative;
          z-index: 1;
        }
        .editor-tab-btn {
          position: relative;
          flex: 1;
          min-width: 0;
          padding: 11px 10px 13px;
          border: 1px solid transparent;
          border-radius: 10px;
          background: rgba(232, 240, 255, 0.02);
          color: rgba(232, 240, 255, 0.38);
          font-family: inherit;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          cursor: pointer;
          transition:
            color 0.22s ease,
            background 0.22s ease,
            border-color 0.22s ease,
            box-shadow 0.22s ease,
            transform 0.18s ease;
          overflow: hidden;
        }
        .editor-tab-btn:not(:disabled):hover {
          color: rgba(232, 240, 255, 0.72);
          background: rgba(232, 240, 255, 0.05);
          border-color: rgba(232, 240, 255, 0.08);
          transform: translateY(-1px);
        }
        .editor-tab-btn--active {
          color: var(--dock-accent);
          background:
            radial-gradient(120% 140% at 50% 0%, var(--dock-active-bg) 0%, rgba(8, 12, 22, 0.55) 68%);
          border-color: var(--dock-active-border);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.07),
            0 0 22px var(--dock-glow),
            0 8px 18px rgba(0, 0, 0, 0.35);
          text-shadow: 0 0 14px var(--dock-glow);
        }
        .editor-tab-btn--active::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            transparent 0%,
            rgba(255, 255, 255, 0.07) 45%,
            transparent 70%
          );
          animation: editorTabBeam 5.5s ease-in-out infinite;
          pointer-events: none;
        }
        .editor-tab-btn__label {
          position: relative;
          z-index: 1;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .editor-tab-btn__beacon {
          position: absolute;
          left: 50%;
          bottom: 5px;
          transform: translateX(-50%) scaleX(0.35);
          width: 72%;
          max-width: 88px;
          height: 2px;
          border-radius: 99px;
          background: var(--dock-accent);
          box-shadow:
            0 0 8px var(--dock-accent),
            0 0 18px var(--dock-glow);
          opacity: 0;
          transition: opacity 0.22s ease, transform 0.22s ease;
          pointer-events: none;
        }
        .editor-tab-btn--active .editor-tab-btn__beacon {
          opacity: 1;
          transform: translateX(-50%) scaleX(1);
        }
        .editor-tab-btn--disabled {
          opacity: 0.42;
          cursor: not-allowed;
          color: rgba(232, 240, 255, 0.18);
        }
        .editor-tab-btn--disabled:hover {
          transform: none;
          background: rgba(232, 240, 255, 0.02);
          border-color: transparent;
        }
        .editor-tab-btn__index {
          display: block;
          font-size: 7px;
          letter-spacing: 0.22em;
          color: rgba(232, 240, 255, 0.22);
          margin-bottom: 3px;
          font-variant-numeric: tabular-nums;
        }
        .editor-tab-btn--active .editor-tab-btn__index {
          color: var(--dock-accent-soft);
        }

        .editor-ai-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          padding: 10px 12px 2px;
        }
        .editor-ai-actions--stacked {
          flex-direction: column;
          flex-wrap: nowrap;
          padding: 0;
          margin-top: 8px;
        }
        .editor-ai-actions--stacked .editor-ai-action-btn {
          flex: none;
          width: 100%;
          min-width: 0;
        }
        .editor-ai-action-btn {
          position: relative;
          flex: 1;
          min-width: min(100%, 210px);
          padding: 12px 14px 15px;
          border: 1px solid var(--dock-active-border);
          border-radius: 10px;
          background:
            radial-gradient(120% 140% at 50% 0%, var(--dock-active-bg) 0%, rgba(8, 12, 22, 0.55) 68%);
          color: var(--dock-accent);
          font-family: inherit;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          line-height: 1.35;
          text-transform: uppercase;
          text-align: center;
          cursor: pointer;
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.07),
            0 0 22px var(--dock-glow),
            0 8px 18px rgba(0, 0, 0, 0.35);
          text-shadow: 0 0 14px var(--dock-glow);
          transition:
            color 0.22s ease,
            background 0.22s ease,
            border-color 0.22s ease,
            box-shadow 0.22s ease,
            transform 0.18s ease;
          overflow: hidden;
        }
        .editor-ai-action-btn:hover {
          transform: translateY(-1px);
          border-color: var(--dock-accent-soft);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.09),
            0 0 28px var(--dock-glow),
            0 10px 22px rgba(0, 0, 0, 0.4);
        }
        .editor-ai-action-btn::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            transparent 0%,
            rgba(255, 255, 255, 0.07) 45%,
            transparent 70%
          );
          animation: editorTabBeam 5.5s ease-in-out infinite;
          pointer-events: none;
        }
        .editor-ai-action-btn__label {
          position: relative;
          z-index: 1;
          display: block;
        }
        .editor-ai-action-btn__beacon {
          position: absolute;
          left: 50%;
          bottom: 5px;
          transform: translateX(-50%) scaleX(1);
          width: 72%;
          max-width: 120px;
          height: 2px;
          border-radius: 99px;
          background: var(--dock-accent);
          box-shadow:
            0 0 8px var(--dock-accent),
            0 0 18px var(--dock-glow);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}

/* ─── Settings strip (always visible) ─── */

function EditorSettingsStrip({
  scene,
  realm,
  tokens,
  canDeleteScene,
  deleteState,
  deleteSubmitting,
  onDeleteClick,
  onColorChange,
  sceneDurationSec,
  contentDurationSec,
  onSceneDurationChange,
  insertMenuOpen,
  onInsertMenuToggle,
  onInsertBefore,
  onInsertAfter,
}: {
  scene: SceneListItem;
  realm: RealmId;
  tokens: RealmThemeTokens;
  canDeleteScene: boolean;
  deleteState: DeleteState;
  deleteSubmitting: boolean;
  onDeleteClick: () => void;
  onColorChange: (color: string | null) => void;
  sceneDurationSec: number | null;
  contentDurationSec: number;
  onSceneDurationChange: (sec: number | null) => void;
  insertMenuOpen: boolean;
  onInsertMenuToggle: () => void;
  onInsertBefore: () => void;
  onInsertAfter: () => void;
}) {
  const isWilds = realm === "wilds";
  const borderColor = isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.25)";
  const colorSwatches = useMemo(() => {
    if (scene.color && !SCENE_COLORS.includes(scene.color)) {
      return [SCENE_COLORS[0], scene.color, ...SCENE_COLORS.slice(1)];
    }
    return SCENE_COLORS;
  }, [scene.color]);

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "6px 10px",
      marginBottom: 6,
      borderRadius: 6,
      background: "rgba(232,240,255,0.02)",
      border: `1px solid ${isWilds ? "rgba(120,200,120,0.1)" : "rgba(126,184,255,0.1)"}`,
      flexShrink: 0,
      flexWrap: "wrap",
    }}>
      {/* Duration */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(232,240,255,0.45)" }}>
          {t(realm, "editor_scene_duration")}
        </span>
        <input
          type="number"
          min={0}
          step={0.5}
          value={sceneDurationSec ?? ""}
          placeholder={contentDurationSec.toFixed(1)}
          onChange={(e) => {
            const val = e.target.value ? parseFloat(e.target.value) : null;
            onSceneDurationChange(val);
          }}
          style={{
            width: 56,
            background: "rgba(232,240,255,0.06)",
            border: `1px solid ${borderColor}`,
            borderRadius: 4,
            padding: "3px 6px",
            color: "#e8f0ff",
            fontSize: 11,
            outline: "none",
          }}
        />
        <span style={{ fontSize: 9, color: "rgba(232,240,255,0.4)" }}>s</span>
      </div>

      {/* Color swatches */}
      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
        {colorSwatches.map((c) => (
          <button
            key={c ?? "none"}
            type="button"
            onClick={() => onColorChange(c)}
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              background: c ?? "rgba(232,240,255,0.08)",
              border: scene.color === c ? `2px solid ${tokens.accent}` : "1px solid rgba(232,240,255,0.15)",
              cursor: "pointer",
              padding: 0,
              position: "relative",
            }}
          >
            {!c && (
              <svg width="8" height="8" viewBox="0 0 10 10" style={{ position: "absolute", inset: 0, margin: "auto" }}>
                <path d="M2 8L8 2" stroke="rgba(232,240,255,0.4)" strokeWidth="1" strokeLinecap="round" />
              </svg>
            )}
          </button>
        ))}
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Insert Star dropdown */}
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={onInsertMenuToggle}
          style={{
            height: 26,
            padding: "0 10px",
            borderRadius: 6,
            border: `1px solid ${isWilds ? "rgba(160,210,100,0.5)" : "rgba(245,186,65,0.5)"}`,
            background: isWilds ? "rgba(160,210,100,0.12)" : "rgba(245,186,65,0.12)",
            color: isWilds ? "rgba(180,230,120,0.9)" : "rgba(245,196,85,0.9)",
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.04em",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {t(realm, "editor_insert_star_menu", "Insert New Star\u2026")}
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {insertMenuOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: 4,
              background: "rgba(10,16,32,0.96)",
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              padding: 4,
              zIndex: 100,
              minWidth: 180,
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}
          >
            <button
              type="button"
              onClick={onInsertBefore}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "none",
                border: "none",
                color: "#e8f0ff",
                fontSize: 11,
                fontWeight: 500,
                textAlign: "left",
                cursor: "pointer",
                borderRadius: 4,
                fontFamily: "inherit",
              }}
            >
              {t(realm, "editor_create_scene_before", "Insert New Star Before")}
            </button>
            <button
              type="button"
              onClick={onInsertAfter}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "none",
                border: "none",
                color: "#e8f0ff",
                fontSize: 11,
                fontWeight: 500,
                textAlign: "left",
                cursor: "pointer",
                borderRadius: 4,
                fontFamily: "inherit",
              }}
            >
              {t(realm, "editor_create_scene_after", "Insert New Star After")}
            </button>
          </div>
        )}
      </div>

      {/* Delete */}
      {canDeleteScene && (
        <button
          type="button"
          onClick={() => void onDeleteClick()}
          disabled={deleteSubmitting}
          style={{
            padding: "3px 8px",
            borderRadius: 4,
            background: deleteState === "confirming" ? "rgba(255,107,107,0.16)" : "rgba(255,107,107,0.06)",
            border: `1px solid ${deleteState === "confirming" ? "rgba(255,107,107,0.7)" : "rgba(255,107,107,0.25)"}`,
            color: deleteState === "confirming" ? "#ff8d8d" : "rgba(255,180,180,0.8)",
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            cursor: deleteSubmitting ? "default" : "pointer",
            opacity: deleteSubmitting ? 0.6 : 1,
          }}
        >
          {deleteState === "confirming"
            ? t(realm, "editor_delete_scene_confirm", "Confirm")
            : t(realm, "hud_delete_control", "Delete")}
        </button>
      )}
    </div>
  );
}

/* ─── Unified workspace (node list + context editor) ─── */

function UnifiedWorkspace({
  scene,
  story,
  scenes,
  realm,
  tokens,
  user,
  textLines,
  selectedLineId,
  selectedEffectId,
  selectedChoiceId,
  textFontFamily,
  textColor,
  textBackgroundColor,
  textFontSize,
  sceneChoices,
  storedChoices,
  gotoSceneId,
  totalDurationMs,
  sceneDurationSec,
  choiceActionIdsRef,
  canRewriteWithAi,
  onRewriteWithAi,
  onLinesChange,
  onSelectLine,
  onSelectEffect,
  onSelectChoice,
  onSelectedChoiceIdChange,
  onTextStyleChange,
  onScrubPreviewToMs,
  onGotoSceneIdChange,
  onChoicesChange,
  onScenesChanged,
  onStoryStructureChange,
  persistChoiceProperties,
  onChoiceArrowResize,
  onShowGeneratorEntry,
}: {
  scene: SceneListItem;
  story: StoryListItem | null;
  scenes: SceneListItem[];
  realm: RealmId;
  tokens: RealmThemeTokens;
  user?: UserProfile | null;
  textLines: TextLine[];
  selectedLineId: string | null;
  selectedEffectId: string | null;
  selectedChoiceId: string | null;
  textFontFamily: string;
  textColor: string;
  textBackgroundColor: string;
  textFontSize: number;
  sceneChoices: SceneChoice[];
  storedChoices: SceneChoice[];
  gotoSceneId: string | null;
  totalDurationMs: number;
  sceneDurationSec: number | null;
  choiceActionIdsRef: MutableRefObject<Record<string, string>>;
  canRewriteWithAi: boolean;
  onRewriteWithAi: () => void;
  onLinesChange: (lines: TextLine[]) => void;
  onSelectLine: (id: string | null) => void;
  onSelectEffect: (id: string | null) => void;
  onSelectChoice: (choiceId: string) => void;
  onSelectedChoiceIdChange: (id: string | null) => void;
  onTextStyleChange: (patch: Record<string, unknown>) => void;
  onScrubPreviewToMs: (ms: number) => void;
  onGotoSceneIdChange: (id: string | null) => Promise<void>;
  onChoicesChange: (choices: SceneChoice[]) => void;
  onScenesChanged?: (scenes: SceneListItem[]) => void;
  onStoryStructureChange?: () => void;
  persistChoiceProperties: (choice: SceneChoice, options?: { refreshGraph?: boolean; immediate?: boolean }) => void;
  onChoiceArrowResize: (choiceId: string, scale: number, rotation: number, persist?: boolean) => void;
  onShowGeneratorEntry?: () => void;
}) {
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";
  const [newNodePickerOpen, setNewNodePickerOpen] = useState(false);
  const [confirmingDeleteNodeId, setConfirmingDeleteNodeId] = useState<string | null>(null);
  const isEmptyStory = isUninitializedStory(scenes);

  const textOnlyLines = textLines.filter((l) => !l.type || l.type === "text");
  const effectLines = textLines.filter((l) => l.type === "sound" || l.type === "image");
  const videoLines = textLines.filter((l) => l.type === "video");
  const creditsLines = textLines.filter((l) => l.type === "credits");
  const logicLines = textLines.filter((l) => l.type === "logic");

  type UnifiedNode = {
    id: string;
    type: NodeType;
    label: string;
    appearMs: number;
  };

  const unifiedNodes: UnifiedNode[] = useMemo(() => {
    const nodes: UnifiedNode[] = [];
    for (const line of textOnlyLines) {
      const stripped = line.html.replace(/<[^>]*>/g, "").trim();
      nodes.push({ id: line.id, type: "text", label: stripped || t(realm, "editor_new_text"), appearMs: line.appear_at_ms });
    }
    for (const line of videoLines) {
      nodes.push({ id: line.id, type: "video", label: t(realm, "editor_new_video"), appearMs: line.appear_at_ms });
    }
    for (const line of effectLines) {
      const isMusic = line.html.toLowerCase().includes("music");
      const nodeType: NodeType = line.type === "image" ? "image" : isMusic ? "music" : "sound";
      const stripped = line.html.replace(/<[^>]*>/g, "").trim();
      nodes.push({ id: line.id, type: nodeType, label: stripped || t(realm, nodeType === "image" ? "editor_new_image" : "editor_new_sound"), appearMs: line.appear_at_ms });
    }
    for (const line of creditsLines) {
      const stripped = line.html.replace(/<[^>]*>/g, "").trim();
      nodes.push({ id: line.id, type: "credits", label: stripped || t(realm, "editor_credits_layer"), appearMs: line.appear_at_ms });
    }
    for (const line of logicLines) {
      const mode = line.logic?.mode === "evaluate" ? t(realm, "editor_logic_mode_evaluate") : t(realm, "editor_logic_mode_set");
      const varName = line.logic?.variable || "";
      nodes.push({ id: line.id, type: "logic", label: varName ? `${mode}: ${varName}` : t(realm, "editor_new_logic"), appearMs: line.appear_at_ms });
    }
    for (const choice of sceneChoices) {
      const choiceLabel = choice.label
        ? t(realm, choice.label, choice.label)
        : t(realm, "editor_new_choice");
      nodes.push({ id: choice.id, type: "choice", label: choiceLabel, appearMs: 0 });
    }
    nodes.sort((a, b) => a.appearMs - b.appearMs);
    return nodes;
  }, [textOnlyLines, videoLines, effectLines, creditsLines, logicLines, sceneChoices, realm]);

  const selectedNodeId = selectedLineId || selectedEffectId || selectedChoiceId;
  const selectedNode = unifiedNodes.find((n) => n.id === selectedNodeId) ?? null;

  function handleNodeSelect(node: UnifiedNode) {
    onSelectLine(null);
    onSelectEffect(null);
    onSelectedChoiceIdChange(null);
    if (node.type === "text" || node.type === "video" || node.type === "credits" || node.type === "logic") {
      onSelectLine(node.id);
    } else if (node.type === "choice") {
      onSelectChoice(node.id);
    } else {
      onSelectEffect(node.id);
    }
    const line = textLines.find((l) => l.id === node.id);
    if (line) onScrubPreviewToMs(line.appear_at_ms);
  }

  function handleDeleteNode(e: React.MouseEvent, nodeId: string, nodeType: NodeType) {
    e.stopPropagation();
    if (confirmingDeleteNodeId === nodeId) {
      setConfirmingDeleteNodeId(null);
      if (nodeType === "text" || nodeType === "video" || nodeType === "credits") {
        const remaining = textLines.filter((l) => l.id !== nodeId);
        if (remaining.filter((l) => !l.type || l.type === "text").length === 0) {
          const freshLine = createDefaultLine();
          onLinesChange([...remaining, freshLine]);
          onSelectLine(freshLine.id);
        } else {
          onLinesChange(remaining);
          if (selectedLineId === nodeId) onSelectLine(remaining.find((l) => !l.type || l.type === "text")?.id ?? null);
        }
      } else if (nodeType === "logic") {
        const remaining = textLines.filter((l) => l.id !== nodeId);
        onLinesChange(remaining);
        if (selectedLineId === nodeId) onSelectLine(null);
      } else if (nodeType === "choice") {
        onSelectedChoiceIdChange(null);
      } else {
        const remaining = textLines.filter((l) => l.id !== nodeId);
        onLinesChange(remaining);
        if (selectedEffectId === nodeId) onSelectEffect(null);
      }
    } else {
      setConfirmingDeleteNodeId(nodeId);
      setTimeout(() => setConfirmingDeleteNodeId((cur) => cur === nodeId ? null : cur), 2500);
    }
  }

  function nodeColor(type: NodeType): string {
    switch (type) {
      case "text": return tokens.textLine;
      case "video": return tokens.videoLine;
      case "sound": return tokens.soundLine;
      case "music": return tokens.musicLine;
      case "image": return tokens.imageLine;
      case "choice": return tokens.choiceLine;
      case "credits": return tokens.creditsLine;
      case "logic": return "#e0a040";
    }
  }

  function nodeIcon(type: NodeType): string {
    switch (type) {
      case "text": return "T";
      case "video": return "\u25B6";
      case "sound": return "\u266A";
      case "music": return "\u266B";
      case "image": return "\u25A1";
      case "choice": return "\u2194";
      case "credits": return "\u2606";
      case "logic": return "\u29BF";
    }
  }

  function handleAddNode(type: NodeType) {
    setNewNodePickerOpen(false);
    if (type === "text") {
      const lastText = textOnlyLines[textOnlyLines.length - 1];
      const nextAppear = lastText ? lastText.appear_at_ms + lastText.duration_ms + lastText.fade_in_ms + lastText.fade_out_ms : 0;
      const newLine = createDefaultLine(nextAppear);
      onLinesChange([...textLines, newLine]);
      onSelectLine(newLine.id);
    } else if (type === "video") {
      const nextAppear = textLines.length > 0 ? Math.max(...textLines.map((l) => l.appear_at_ms + l.duration_ms)) : 0;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "video",
        html: "",
        appear_at_ms: nextAppear,
        fade_in_ms: 0,
        fade_out_ms: 0,
        duration_ms: 5000,
        width_pct: 100,
        height_pct: 100,
      };
      onLinesChange([...textLines, newLine]);
      onSelectLine(newLine.id);
    } else if (type === "sound" || type === "music") {
      const nextAppear = textLines.length > 0 ? Math.max(...textLines.map((l) => l.appear_at_ms + l.duration_ms)) : 0;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "sound",
        html: type === "music" ? "Music" : "Sound",
        appear_at_ms: nextAppear,
        fade_in_ms: 500,
        fade_out_ms: 500,
        duration_ms: 5000,
      };
      onLinesChange([...textLines, newLine]);
      onSelectEffect(newLine.id);
    } else if (type === "image") {
      const nextAppear = textLines.length > 0 ? Math.max(...textLines.map((l) => l.appear_at_ms + l.duration_ms)) : 0;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "image",
        html: "Image",
        appear_at_ms: nextAppear,
        fade_in_ms: 500,
        fade_out_ms: 500,
        duration_ms: 3000,
        width_pct: 50,
        height_pct: 50,
        position_x: 25,
        position_y: 25,
      };
      onLinesChange([...textLines, newLine]);
      onSelectEffect(newLine.id);
    } else if (type === "choice") {
      onSelectedChoiceIdChange("__new__");
    } else if (type === "logic") {
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "logic",
        html: "",
        appear_at_ms: 0,
        fade_in_ms: 0,
        fade_out_ms: 0,
        duration_ms: 0,
        persist: true,
        logic: { mode: "set", variable: "" },
      };
      onLinesChange([...textLines, newLine]);
      onSelectLine(newLine.id);
    } else if (type === "credits") {
      const directorName = user?.username ?? user?.name ?? "";
      const prefix = t(realm, "chosen_by", "Chosen by");
      const html = directorName
        ? `<p style="text-align:left">${prefix} ${directorName}</p>`
        : `<p style="text-align:left">${prefix}</p>`;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "credits",
        html,
        appear_at_ms: 0,
        fade_in_ms: 800,
        fade_out_ms: 800,
        duration_ms: 5000,
        width_pct: 30,
        height_pct: 10,
        position_x: 2,
        position_y: 88,
      };
      onLinesChange([...textLines, newLine]);
      onSelectLine(newLine.id);
    }
  }

  const NEW_NODE_OPTIONS: { type: NodeType; key: string }[] = [
    { type: "text", key: "editor_new_text" },
    { type: "choice", key: "editor_new_choice" },
    { type: "image", key: "editor_new_image" },
    { type: "video", key: "editor_new_video" },
    { type: "music", key: "editor_new_music" },
    { type: "sound", key: "editor_new_sound" },
    { type: "logic", key: "editor_new_logic" },
    { type: "credits", key: "editor_new_credits" },
  ];

  const listPane = (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      padding: 10,
      background: "rgba(232,240,255,0.02)",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(232,240,255,0.5)", marginBottom: 8 }}>
        {t(realm, "editor_node_list_section", "Layers")}
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {unifiedNodes.map((node) => {
          const selected = node.id === selectedNodeId;
          const color = nodeColor(node.type);
          return (
            <button
              key={node.id}
              type="button"
              onClick={() => handleNodeSelect(node)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 8px",
                marginBottom: 2,
                borderRadius: 5,
                border: selected ? `1px solid ${color}55` : "1px solid transparent",
                background: selected ? `${color}15` : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <span style={{ width: 18, height: 18, borderRadius: 3, background: `${color}22`, border: `1px solid ${color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color, flexShrink: 0, fontWeight: 700 }}>
                {nodeIcon(node.type)}
              </span>
              <span style={{ flex: 1, fontSize: 11, color: selected ? "#e8f0ff" : "rgba(232,240,255,0.7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {node.label}
              </span>
              <span style={{ fontSize: 9, color: "rgba(232,240,255,0.35)", flexShrink: 0 }}>
                {(node.appearMs / 1000).toFixed(1)}s
              </span>
              <span
                role="button"
                onClick={(e) => handleDeleteNode(e, node.id, node.type)}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  background: confirmingDeleteNodeId === node.id ? "rgba(255,107,107,0.2)" : "transparent",
                  border: confirmingDeleteNodeId === node.id ? "1px solid rgba(255,107,107,0.6)" : "1px solid transparent",
                  color: confirmingDeleteNodeId === node.id ? "#ff8d8d" : "rgba(255,150,150,0.5)",
                }}
                title={confirmingDeleteNodeId === node.id
                  ? t(realm, "editor_delete_confirm", "Click again to confirm")
                  : t(realm, "editor_remove_layer", "Remove")}
              >
                ×
              </span>
            </button>
          );
        })}
      </div>
      {/* New... button */}
      <div style={{ position: "relative", marginTop: 6 }}>
        <button
          type="button"
          onClick={() => {
            setNewNodePickerOpen((v) => !v);
          }}
          style={{
            width: "100%",
            padding: "7px 0",
            borderRadius: 6,
            border: `1px dashed ${sectionBorder}`,
            background: "none",
            color: tokens.accent,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t(realm, "editor_new_node", "New\u2026")}
        </button>
        {newNodePickerOpen && (
          <div style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            marginBottom: 4,
            background: "rgba(10,16,32,0.97)",
            border: `1px solid ${sectionBorder}`,
            borderRadius: 8,
            padding: 4,
            zIndex: 50,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.4)",
          }}>
            {NEW_NODE_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                type="button"
                onClick={() => handleAddNode(opt.type)}
                style={{
                  width: "100%",
                  padding: "7px 10px",
                  background: "none",
                  border: "none",
                  color: "#e8f0ff",
                  fontSize: 11,
                  fontWeight: 500,
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: 4,
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ width: 16, height: 16, borderRadius: 3, background: `${nodeColor(opt.type)}22`, border: `1px solid ${nodeColor(opt.type)}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: nodeColor(opt.type), fontWeight: 700 }}>
                  {nodeIcon(opt.type)}
                </span>
                {t(realm, opt.key)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const selectedLine = selectedLineId ? textLines.find((l) => l.id === selectedLineId) ?? null : null;
  const selectedEffect = selectedEffectId ? textLines.find((l) => l.id === selectedEffectId) ?? null : null;
  const selectedChoice = selectedChoiceId ? sceneChoices.find((c) => c.id === selectedChoiceId) ?? null : null;

  let editorPane: ReactNode;
  if (selectedLine && selectedLine.type === "logic") {
    editorPane = (
      <LogicEditorPane
        realm={realm}
        line={selectedLine}
        textLines={textLines}
        scenes={scenes}
        onLinesChange={onLinesChange}
      />
    );
  } else if (selectedLine && (!selectedLine.type || selectedLine.type === "text" || selectedLine.type === "credits")) {
    editorPane = (
      <TextAuthoringTabContent
        scene={scene}
        realm={realm}
        textLines={textLines}
        selectedLineId={selectedLineId}
        textFontFamily={textFontFamily}
        textColor={textColor}
        textBackgroundColor={textBackgroundColor}
        textFontSize={textFontSize}
        canRewriteWithAi={canRewriteWithAi}
        onRewriteWithAi={onRewriteWithAi}
        onLinesChange={onLinesChange}
        onSelectLine={onSelectLine}
        onTextStyleChange={onTextStyleChange}
        onScrubPreviewToMs={onScrubPreviewToMs}
      />
    );
  } else if (selectedEffect || (selectedLine && (selectedLine.type === "video" || selectedLine.type === "sound" || selectedLine.type === "image"))) {
    editorPane = (
      <EffectsTabContent
        scene={scene}
        realm={realm}
        user={user ?? null}
        textLines={textLines}
        selectedEffectId={selectedEffectId || selectedLineId}
        onSelectedEffectIdChange={onSelectEffect}
        onLinesChange={onLinesChange}
        onScrubPreviewToMs={onScrubPreviewToMs}
      />
    );
  } else if (selectedChoice) {
    editorPane = (
      <ChoicesTabContent
        scene={scene}
        story={story}
        choices={sceneChoices}
        storedChoices={storedChoices}
        scenes={scenes}
        realm={realm}
        totalDurationMs={totalDurationMs}
        sceneDurationSec={sceneDurationSec}
        gotoSceneId={gotoSceneId}
        selectedChoiceId={selectedChoiceId}
        choiceActionIdsRef={choiceActionIdsRef}
        onGotoSceneIdChange={onGotoSceneIdChange}
        onChoicesChange={onChoicesChange}
        onScenesChanged={onScenesChanged}
        onStoryStructureChange={onStoryStructureChange}
        onScrubPreviewToMs={onScrubPreviewToMs}
        onSelectChoice={onSelectChoice}
        onSelectedChoiceIdChange={onSelectedChoiceIdChange}
        persistChoiceProperties={persistChoiceProperties}
        onChoiceArrowResize={onChoiceArrowResize}
      />
    );
  } else {
    editorPane = (
      <EditorPanePlaceholder realm={realm} messageKey="editor_select_node_hint" fallback="Select a layer to edit" />
    );
  }

  return (
    <EditorSplitWorkspace
      listPane={listPane}
      editorPane={<div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>{editorPane}</div>}
    />
  );
}

/* ─── Shared editor layout helpers ─── */

function EditorSplitWorkspace({
  listPane,
  editorPane,
}: {
  listPane: ReactNode;
  editorPane: ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 8, flex: 1, minHeight: 0, overflow: "hidden" }}>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {listPane}
      </div>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {editorPane}
      </div>
    </div>
  );
}

function EditorPanePlaceholder({
  realm,
  messageKey,
  fallback,
}: {
  realm: RealmId;
  messageKey: string;
  fallback: string;
}) {
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";

  return (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      background: "rgba(232,240,255,0.02)",
      flex: 1,
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      textAlign: "center",
      fontSize: 12,
      color: "rgba(232,240,255,0.35)",
      lineHeight: 1.5,
    }}>
      {t(realm, messageKey, fallback)}
    </div>
  );
}

function SceneEditorNavBar({
  realm,
  user = null,
  scenes,
  activeScene,
  activeSceneIndex,
  canGoPrev,
  canGoNext,
  accent,
  accentDim,
  borderColor,
  editingTitle,
  titleDraft,
  createSceneFlow,
  createTitleDraft,
  createSubmitting,
  onNavigate,
  onStartEditTitle,
  onTitleDraftChange,
  onSaveTitle,
  onCancelEditTitle,
  onCreateSceneFlowChange,
  onCreateTitleDraftChange,
  onSubmitCreateScene,
  onCancelCreate,
  onChooseCreateUpload,
  onChooseGenerate,
}: {
  realm: RealmId;
  user?: UserProfile | null;
  scenes: SceneListItem[];
  activeScene: SceneListItem;
  activeSceneIndex: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  accent: string;
  accentDim: string;
  borderColor: string;
  editingTitle: boolean;
  titleDraft: string;
  createSceneFlow: CreateSceneFlow;
  createTitleDraft: string;
  createSubmitting: boolean;
  onNavigate: (index: number) => void;
  onStartEditTitle: () => void;
  onTitleDraftChange: (value: string) => void;
  onSaveTitle: () => void;
  onCancelEditTitle: () => void;
  onCreateSceneFlowChange: (flow: CreateSceneFlow) => void;
  onCreateTitleDraftChange: (v: string) => void;
  onSubmitCreateScene: () => void;
  onCancelCreate: () => void;
  onChooseCreateUpload: () => void;
  onChooseGenerate: () => void;
}) {
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.28)" : "rgba(126,184,255,0.28)";
  const sectionBg = isWilds ? "rgba(20,40,20,0.85)" : "rgba(10,16,32,0.85)";
  const disabledArrowStroke = "rgba(232,240,255,0.12)";

  const navArrowButton = (
    direction: "prev" | "next",
    enabled: boolean,
    onClick: () => void,
    label: string,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      style={{
        background: "none",
        border: "none",
        cursor: enabled ? "pointer" : "default",
        opacity: enabled ? 1 : 0.08,
        padding: 4,
        flexShrink: 0,
        transition: "opacity 180ms ease",
      }}
      title={label}
      aria-label={label}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        {direction === "prev" ? (
          <path
            d="M10 3L5 8l5 5"
            stroke={enabled ? accent : disabledArrowStroke}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M6 3l5 5-5 5"
            stroke={enabled ? accent : disabledArrowStroke}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </button>
  );

  return (
    <div
      style={{
        flexShrink: 0,
        marginBottom: 10,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${sectionBorder}`,
        background: sectionBg,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
      aria-label={t(realm, "editor_scene_nav")}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {navArrowButton(
          "prev",
          canGoPrev,
          () => { if (canGoPrev) onNavigate(activeSceneIndex - 1); },
          t(realm, "editor_prev_frame", "Previous scene"),
        )}

        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
          {activeScene.is_title ? (
            <span
              style={{
                flexShrink: 0,
                fontSize: 8,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: accent,
                padding: "2px 6px",
                borderRadius: 999,
                border: `1px solid ${accentDim}`,
                background: "rgba(10,16,32,0.6)",
              }}
            >
              {t(realm, "editor_title_scene")}
            </span>
          ) : null}

          {editingTitle ? (
            <div style={{ display: "flex", gap: 4, alignItems: "center", minWidth: 0, flex: 1 }}>
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => onTitleDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") onSaveTitle();
                  if (e.key === "Escape") onCancelEditTitle();
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#e8f0ff",
                  background: "rgba(232,240,255,0.06)",
                  border: `1px solid ${borderColor}`,
                  borderRadius: 6,
                  padding: "3px 8px",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              <button
                type="button"
                onClick={onSaveTitle}
                style={{
                  background: accent,
                  border: "none",
                  borderRadius: 4,
                  padding: "3px 8px",
                  color: "#04070d",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                  flexShrink: 0,
                  fontFamily: "inherit",
                }}
              >
                {t(realm, "editor_save")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onStartEditTitle}
              title={t(realm, "editor_scene_title_hint")}
              style={{
                flex: 1,
                minWidth: 0,
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: "#e8f0ff",
                textAlign: "left",
                cursor: "pointer",
                fontFamily: "inherit",
                borderBottom: "1px dashed rgba(232,240,255,0.22)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeScene.title}
            </button>
          )}
        </div>

        {navArrowButton(
          "next",
          canGoNext,
          () => { if (canGoNext) onNavigate(activeSceneIndex + 1); },
          t(realm, "editor_next_frame", "Next scene"),
        )}

        {createSceneFlow === null ? (
          <button
            type="button"
            onClick={() => onCreateSceneFlowChange("choice")}
            style={{
              flexShrink: 0,
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px dashed ${sectionBorder}`,
              background: isWilds ? "rgba(120,200,120,0.06)" : "rgba(126,184,255,0.06)",
              color: accent,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <line x1="7" y1="2" x2="7" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="2" y1="7" x2="12" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {t(realm, "editor_create_scene_plain")}
          </button>
        ) : (
          <button
            type="button"
            onClick={onCancelCreate}
            style={{
              flexShrink: 0,
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${sectionBorder}`,
              background: "transparent",
              color: "rgba(232,240,255,0.6)",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {t(realm, "editor_cancel")}
          </button>
        )}
      </div>

      {createSceneFlow === "choice" ? (
        <GeneratorEntry
          realm={realm}
          user={user}
          compact
          onCreateUpload={onChooseCreateUpload}
          onGenerate={onChooseGenerate}
        />
      ) : null}

      {createSceneFlow === "blank" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            type="text"
            value={createTitleDraft}
            onChange={(e) => onCreateTitleDraftChange(e.target.value)}
            maxLength={255}
            placeholder={t(realm, "film_title_placeholder")}
            autoFocus
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmitCreateScene();
              }
              if (e.key === "Escape") onCancelCreate();
            }}
            style={{
              width: "100%",
              background: isWilds ? "rgba(90,180,90,0.08)" : "rgba(126,184,255,0.08)",
              border: `1px solid ${isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.3)"}`,
              borderRadius: 6,
              color: "#e8f0ff",
              padding: "7px 10px",
              fontSize: 13,
              outline: "none",
              fontFamily: "inherit",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onCancelCreate}
              disabled={createSubmitting}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: 12,
                fontWeight: 600,
                color: "rgba(232,240,255,0.65)",
                background: "transparent",
                border: `1px solid ${isWilds ? "rgba(90,180,90,0.25)" : "rgba(126,184,255,0.25)"}`,
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t(realm, "editor_cancel")}
            </button>
            <button
              type="button"
              onClick={onSubmitCreateScene}
              disabled={createSubmitting || !createTitleDraft.trim()}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: 12,
                fontWeight: 600,
                color: "#e8f0ff",
                background: isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)",
                border: `1px solid ${isWilds ? "rgba(90,180,90,0.5)" : "rgba(126,184,255,0.5)"}`,
                borderRadius: 6,
                cursor: createSubmitting ? "wait" : "pointer",
                opacity: createSubmitting ? 0.75 : 1,
                fontFamily: "inherit",
              }}
            >
              {createSubmitting ? t(realm, "editor_ai_generating") : t(realm, "editor_create_scene")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Action bar helpers ─── */

function editorDockVars(realm: RealmId): CSSProperties {
  const isWilds = realm === "wilds";
  return (isWilds
    ? {
        "--dock-accent": "#8fd88f",
        "--dock-accent-soft": "rgba(143, 216, 143, 0.75)",
        "--dock-glow": "rgba(120, 200, 120, 0.42)",
        "--dock-ambient": "rgba(90, 180, 90, 0.12)",
        "--dock-border": "rgba(120, 200, 120, 0.28)",
        "--dock-rim": "rgba(120, 200, 120, 0.35)",
        "--dock-active-bg": "rgba(120, 200, 120, 0.16)",
        "--dock-active-border": "rgba(120, 200, 120, 0.38)",
      }
    : {
        "--dock-accent": "#9ec8ff",
        "--dock-accent-soft": "rgba(158, 200, 255, 0.75)",
        "--dock-glow": "rgba(126, 184, 255, 0.48)",
        "--dock-ambient": "rgba(126, 184, 255, 0.14)",
        "--dock-border": "rgba(126, 184, 255, 0.3)",
        "--dock-rim": "rgba(126, 184, 255, 0.4)",
        "--dock-active-bg": "rgba(126, 184, 255, 0.18)",
        "--dock-active-border": "rgba(126, 184, 255, 0.42)",
      }) as CSSProperties;
}

const AI_ACTION_ACCENT_VARS: CSSProperties = {
  "--dock-accent": "#ffb366",
  "--dock-accent-soft": "rgba(255, 179, 102, 0.75)",
  "--dock-glow": "rgba(255, 160, 80, 0.45)",
  "--dock-ambient": "rgba(255, 160, 80, 0.12)",
  "--dock-border": "rgba(255, 160, 80, 0.3)",
  "--dock-rim": "rgba(255, 160, 80, 0.35)",
  "--dock-active-bg": "rgba(255, 160, 80, 0.16)",
  "--dock-active-border": "rgba(255, 160, 80, 0.38)",
} as CSSProperties;

function EditorAiActionsBar({
  realm,
  children,
  stacked = false,
  accent,
}: {
  realm: RealmId;
  children: ReactNode;
  stacked?: boolean;
  accent?: "ai";
}) {
  const vars = accent === "ai" ? AI_ACTION_ACCENT_VARS : editorDockVars(realm);
  return (
    <div
      className={["editor-ai-actions", stacked ? "editor-ai-actions--stacked" : ""].filter(Boolean).join(" ")}
      data-realm={realm}
      style={vars}
    >
      {children}
    </div>
  );
}

function EditorAiActionButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="editor-ai-action-btn" onClick={onClick}>
      <span className="editor-ai-action-btn__label">{children}</span>
      <span className="editor-ai-action-btn__beacon" aria-hidden />
    </button>
  );
}


/* ─── Authoring + Media Preview Tabs ─── */

function TextAuthoringTabContent({
  realm,
  textLines,
  selectedLineId,
  textFontFamily,
  textColor,
  textBackgroundColor,
  textFontSize,
  canRewriteWithAi,
  onRewriteWithAi,
  onLinesChange,
  onSelectLine,
  onTextStyleChange,
  onScrubPreviewToMs,
}: {
  scene: SceneListItem;
  realm: RealmId;
  textLines: TextLine[];
  selectedLineId: string | null;
  textFontFamily: string;
  textColor: string;
  textBackgroundColor: string;
  textFontSize: number;
  canRewriteWithAi: boolean;
  onRewriteWithAi: () => void;
  onLinesChange: (lines: TextLine[]) => void;
  onSelectLine: (id: string | null) => void;
  onTextStyleChange: (next: {
    fontFamily?: string;
    textColor?: string;
    backgroundColor?: string;
    fontSize?: number;
  }) => void;
  onScrubPreviewToMs: (ms: number) => void;
}) {
  const editorRef = useRef<any>(null);
  const [editorActive, setEditorActive] = useState({
    bold: false, italic: false, strike: false,
    bulletList: false, orderedList: false,
    alignLeft: true, alignCenter: false, alignRight: false,
  });

  const handleEditorReady = useCallback((editor: any) => {
    editorRef.current = editor;
    const sync = () => {
      setEditorActive({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        strike: editor.isActive("strike"),
        bulletList: editor.isActive("bulletList"),
        orderedList: editor.isActive("orderedList"),
        alignLeft: editor.isActive({ textAlign: "left" }),
        alignCenter: editor.isActive({ textAlign: "center" }),
        alignRight: editor.isActive({ textAlign: "right" }),
      });
    };
    editor.on("selectionUpdate", sync);
    editor.on("transaction", sync);
    sync();
  }, []);

  const textOnlyLines = textLines.filter((l) => !l.type || l.type === "text");
  const effectLines = textLines.filter((l) => l.type === "sound" || l.type === "image");
  const selectedLine = textOnlyLines.find((l) => l.id === selectedLineId) ?? null;

  const handleLineHtmlChange = useCallback(
    (html: string) => {
      if (!selectedLineId) return;
      const updated = textLines.map((l) =>
        l.id === selectedLineId ? { ...l, html } : l,
      );
      onLinesChange(updated);
    },
    [selectedLineId, textLines, onLinesChange],
  );

  const handleLineUpdate = useCallback(
    (id: string, update: Partial<TextLine>) => {
      const updated = textLines.map((l) =>
        l.id === id ? { ...l, ...update } : l,
      );
      onLinesChange(updated);
      if (update.appear_at_ms != null) {
        const line = updated.find((l) => l.id === id);
        if (line) onScrubPreviewToMs(previewMsWhenVisible(line));
      }
    },
    [textLines, onLinesChange, onScrubPreviewToMs],
  );

  const handleSelectLine = useCallback(
    (id: string | null) => {
      onSelectLine(id);
      if (!id) return;
      const line = textLines.find((l) => l.id === id);
      if (line) onScrubPreviewToMs(previewMsWhenVisible(line));
    },
    [textLines, onSelectLine, onScrubPreviewToMs],
  );

  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleAddLine = useCallback(() => {
    const lastTextLine = textOnlyLines[textOnlyLines.length - 1];
    const nextAppear = lastTextLine
      ? lastTextLine.appear_at_ms + lastTextLine.fade_in_ms + lastTextLine.duration_ms + lastTextLine.fade_out_ms
      : 0;
    const newLine = createDefaultLine(nextAppear);
    const updated = [...textLines, newLine];
    onLinesChange(updated);
    onSelectLine(newLine.id);
    onScrubPreviewToMs(previewMsWhenVisible(newLine));
  }, [textOnlyLines, textLines, onLinesChange, onSelectLine, onScrubPreviewToMs]);

  const handleAddVideo = useCallback(async (file: File) => {
    try {
      const cost = await api.credits.uploadCost(file.size);
      if (!cost.can_afford) {
        return;
      }
      const { key, uploadUrl } = await api.media.uploadUrl({
        filename: file.name,
        contentType: file.type || "video/mp4",
      });
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener("error", () => reject(new Error("Upload network error")));
        xhr.send(file);
      });
      const media = await api.media.register({
        key,
        s3_key: key,
        filename: file.name,
        content_type: file.type || "video/mp4",
        title: file.name,
        file_size: file.size,
      });
      const lastLine = textLines[textLines.length - 1];
      const nextAppear = lastLine
        ? lastLine.appear_at_ms + lastLine.fade_in_ms + lastLine.duration_ms + lastLine.fade_out_ms
        : 0;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: "video",
        media_id: media.id,
        html: file.name,
        appear_at_ms: nextAppear,
        fade_in_ms: 500,
        fade_out_ms: 500,
        duration_ms: 5000,
        position_x: 0,
        position_y: 0,
        width_pct: 100,
        height_pct: 100,
        persist: false,
      };
      const updated = [...textLines, newLine];
      onLinesChange(updated);
      onSelectLine(newLine.id);
      onScrubPreviewToMs(previewMsWhenVisible(newLine));
    } catch { /* upload error — non-critical */ }
  }, [textLines, onLinesChange, onSelectLine, onScrubPreviewToMs]);

  const handleVideoFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleAddVideo(file);
    if (videoInputRef.current) videoInputRef.current.value = "";
  }, [handleAddVideo]);

  const handleDeleteLine = useCallback(
    (id: string) => {
      if (textOnlyLines.length <= 1) {
        const freshLine = createDefaultLine();
        const effectsOnly = textLines.filter((l) => l.type === "sound" || l.type === "image");
        onLinesChange([...effectsOnly, freshLine]);
        onSelectLine(freshLine.id);
        onScrubPreviewToMs(previewMsWhenVisible(freshLine));
        return;
      }
      const updated = textLines.filter((l) => l.id !== id);
      onLinesChange(updated);
      if (selectedLineId === id) {
        const remainingText = updated.filter((l) => !l.type || l.type === "text");
        onSelectLine(remainingText[0]?.id ?? null);
      }
    },
    [textOnlyLines, textLines, selectedLineId, onLinesChange, onSelectLine, onScrubPreviewToMs],
  );

  const handleEmojiInsert = useCallback(
    (emoji: string) => {
      if (!selectedLineId) return;
      const updated = textLines.map((l) =>
        l.id === selectedLineId ? { ...l, html: l.html + emoji } : l,
      );
      onLinesChange(updated);
    },
    [selectedLineId, textLines, onLinesChange],
  );

  const handleTextReorder = useCallback(
    (reorderedTextLines: TextLine[]) => {
      const effectLines = textLines.filter((l) => l.type === "sound" || l.type === "image");
      onLinesChange([...reorderedTextLines, ...effectLines]);
    },
    [textLines, onLinesChange],
  );

  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";
  const sectionLabelColor = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";

  if (!selectedLine) {
    return (
      <EditorPanePlaceholder
        realm={realm}
        messageKey="editor_select_line_hint"
        fallback="Select a line to edit"
      />
    );
  }

  return (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      background: "rgba(232,240,255,0.02)",
      overflow: "hidden",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        padding: "6px 12px",
        borderBottom: `1px solid ${sectionBorder}`,
        background: isWilds ? "rgba(120,200,120,0.05)" : "rgba(126,184,255,0.05)",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: sectionLabelColor,
        }}>
          {t(realm, "editor_line_editor_label", "Editor")} — {t(realm, "editor_line_number", "Line")} {textOnlyLines.findIndex((l) => l.id === selectedLine.id) + 1}
        </span>
      </div>

      <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(232,240,255,0.1)", flexShrink: 0 }}>
        <FormattingToolbar
          realm={realm}
          editorHtml={selectedLine.html}
          fontFamily={textFontFamily}
          textColor={textColor}
          backgroundColor={textBackgroundColor}
          fontSize={textFontSize}
          onStyleChange={onTextStyleChange}
          onBold={() => { editorRef.current?.chain().focus().toggleBold().run(); }}
          onItalic={() => { editorRef.current?.chain().focus().toggleItalic().run(); }}
          onStrike={() => { editorRef.current?.chain().focus().toggleStrike().run(); }}
          onBulletList={() => { editorRef.current?.chain().focus().toggleBulletList().run(); }}
          onOrderedList={() => { editorRef.current?.chain().focus().toggleOrderedList().run(); }}
          onAlignLeft={() => { editorRef.current?.chain().focus().setTextAlign("left").run(); }}
          onAlignCenter={() => { editorRef.current?.chain().focus().setTextAlign("center").run(); }}
          onAlignRight={() => { editorRef.current?.chain().focus().setTextAlign("right").run(); }}
          onEmojiInsert={handleEmojiInsert}
          activeStates={editorActive}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <RichTextLineEditor
          html={selectedLine.html}
          realm={realm}
          onChange={handleLineHtmlChange}
          onEditorReady={handleEditorReady}
        />
      </div>

      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(232,240,255,0.1)",
          background: "rgba(232,240,255,0.02)",
          padding: "10px 12px",
        }}
      >
        <LineTimingControls
          line={selectedLine}
          realm={realm}
          onUpdate={(update) => handleLineUpdate(selectedLine.id, update)}
        />
      </div>

      {canRewriteWithAi ? (
        <div style={{ flexShrink: 0, padding: "8px 10px", borderTop: `1px solid ${sectionBorder}` }}>
          <EditorAiActionsBar realm={realm} stacked accent="ai">
            <EditorAiActionButton onClick={onRewriteWithAi}>
              {t(realm, "chuzi_ai_scene_overwrite")}
            </EditorAiActionButton>
          </EditorAiActionsBar>
        </div>
      ) : null}
    </div>
  );
}

function isImageUrl(url: string): boolean {
  const path = new URL(url).pathname.toLowerCase();
  return /\.(png|jpe?g|gif|webp|avif|svg)$/.test(path);
}

/** @deprecated Unused — was the standalone media preview tab */
function _MediaTabContent_REMOVED(): null { return null; }
void _MediaTabContent_REMOVED;

function _MediaTabContent_Original({
  scene,
  story,
  realm,
  mediaUrl,
  loading,
  textHtml,
  textFontFamily,
  textColor,
  textBackgroundColor,
  allowUpload,
  coverboxUrl,
}: {
  scene: SceneListItem;
  story: StoryListItem | null;
  realm: RealmId;
  mediaUrl: string | null;
  loading: boolean;
  textHtml: string;
  textFontFamily: string;
  textColor: string;
  textBackgroundColor: string;
  allowUpload: boolean;
  coverboxUrl?: string | null;
}) {
  const navigate = useNavigate();

  if (loading) {
    return <LoadingIndicator realm={realm} />;
  }

  if (mediaUrl) {
    if (isImageUrl(mediaUrl)) {
      return (
        <div style={{ width: "100%", borderRadius: 10, overflow: "hidden" }}>
          <img
            src={mediaUrl}
            alt="Scene media"
            style={{ width: "100%", display: "block", objectFit: "cover" }}
          />
        </div>
      );
    }
    return <VideoPlayer src={mediaUrl} realm={realm} />;
  }

  if (scene.media_id) {
    return (
      <div style={{ textAlign: "center", color: "rgba(232,240,255,0.5)", fontSize: 13, padding: 20 }}>
        {t(realm, "editor_no_media")}
      </div>
    );
  }

  const hasText = textHtml.replace(/<[^>]*>/g, "").trim().length > 0;
  if (hasText) {
    return (
      <div
        style={{
          width: "100%",
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid rgba(232,240,255,0.2)",
          background: textBackgroundColor,
          minHeight: 260,
          padding: 16,
        }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: textHtml }}
          style={{
            color: textColor,
            fontFamily: textFontFamily,
            fontSize: 16,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />
      </div>
    );
  }

  const isDefaultSlate = scene.is_title && !scene.media_id;
  const creatorAvatarUrl =
    story && typeof story.creator === "object" && story.creator
      ? ("avatar_url" in story.creator && typeof story.creator.avatar_url === "string"
          ? story.creator.avatar_url
          : null)
      : null;

  if (isDefaultSlate) {
    return (
      <DefaultSceneSlate
        variant={scene.is_title ? "title" : "end"}
        storyTitle={story?.title ?? scene.title}
        directorName={story?.creator?.name ?? null}
        directorId={story?.creator?.id ?? null}
        directorAvatarUrl={creatorAvatarUrl}
        realm={realm}
        interactive
        backgroundUrl={coverboxUrl ?? null}
        onDirectorClick={(id) => navigate(`/directors/${id}`)}
      />
    );
  }

  if (allowUpload) {
    return (
      <div style={{ textAlign: "center", color: "rgba(232,240,255,0.5)", fontSize: 13, padding: 20 }}>
        {t(realm, "editor_media_upload")}
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", color: "rgba(232,240,255,0.5)", fontSize: 13, padding: 20 }}>
      {t(realm, "editor_media_preview_disabled", "Add text to enable preview.")}
    </div>
  );
}

/* ─── Effects Tab ─── */

function EffectsTabContent({
  scene,
  realm,
  user,
  textLines,
  selectedEffectId,
  onSelectedEffectIdChange,
  onLinesChange,
  onScrubPreviewToMs,
}: {
  scene: SceneListItem;
  realm: RealmId;
  user: UserProfile | null;
  textLines: TextLine[];
  selectedEffectId: string | null;
  onSelectedEffectIdChange: (id: string | null) => void;
  onLinesChange: (lines: TextLine[]) => void;
  onScrubPreviewToMs: (ms: number) => void;
}) {
  const { openSpendingModal } = useSpendingModal();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [addFlowType, setAddFlowType] = useState<"sound" | "fx" | null>(null);
  const [addFlowStep, setAddFlowStep] = useState<"select" | "describe" | "share">("select");
  const [effectDescription, setEffectDescription] = useState("");
  const [effectShare, setEffectShare] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const tokens = getThemeTokens(realm);
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";
  const sectionLabelColor = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";

  const effectLines = textLines.filter((l) => l.type === "sound" || l.type === "image");
  const selectedEffect = effectLines.find((l) => l.id === selectedEffectId) ?? null;

  const handleUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setUploadProgress(0);
    try {
      const cost = await api.credits.uploadCost(file.size);
      if (!cost.can_afford) {
        const request = await buildSpendingModalRequest(
          user,
          "create",
          cost.credits_needed,
        );
        if (request) {
          openSpendingModal({
            ...request,
            onSuccess: () => {
              void handleUpload(file);
            },
          });
          setUploadProgress(null);
          return;
        }
        setUploadError(t(realm, "spending_insufficient_credits"));
        setUploadProgress(null);
        return;
      }

      const { key, uploadUrl } = await api.media.uploadUrl({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
      });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 90));
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener("error", () => reject(new Error("Upload network error")));
        xhr.send(file);
      });

      setUploadProgress(95);
      const media = await api.media.register({
        key,
        s3_key: key,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        title: file.name,
        file_size: file.size,
      });

      setUploadProgress(100);

      const lineType: LineType = file.type.startsWith("audio/") ? "sound" : "image";
      const lastLine = textLines[textLines.length - 1];
      const nextAppear = lastLine
        ? lastLine.appear_at_ms + lastLine.fade_in_ms + lastLine.duration_ms + lastLine.fade_out_ms
        : 0;
      const newLine: TextLine = {
        id: crypto.randomUUID(),
        type: lineType,
        media_id: media.id,
        html: file.name,
        appear_at_ms: nextAppear,
        fade_in_ms: 500,
        fade_out_ms: 500,
        duration_ms: 3000,
        position_x: 35,
        position_y: 35,
        width_pct: 30,
        height_pct: 30,
        persist: false,
      };
      const updated = [...textLines, newLine];
      onLinesChange(updated);
      onSelectedEffectIdChange(newLine.id);
      onScrubPreviewToMs(previewMsWhenVisible(newLine));

      setTimeout(() => { setUploadProgress(null); }, 400);
    } catch (err: unknown) {
      const opened = await handleInsufficientCredits(
        err,
        user,
        openSpendingModal,
        () => handleUpload(file),
      );
      if (opened) {
        setUploadProgress(null);
        return;
      }
      setUploadError(
        isInsufficientCreditsError(err)
          ? t(realm, "spending_insufficient_credits")
          : err instanceof Error
            ? err.message
            : "Upload failed",
      );
      setUploadProgress(null);
    }
  }, [
    textLines,
    onLinesChange,
    onSelectedEffectIdChange,
    onScrubPreviewToMs,
    user,
    openSpendingModal,
    realm,
  ]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (inputRef.current) inputRef.current.value = "";
  }, [handleUpload]);

  const handleEffectUpdate = useCallback((id: string, update: Partial<TextLine>) => {
    const updated = textLines.map((l) => l.id === id ? { ...l, ...update } : l);
    onLinesChange(updated);
    if (update.appear_at_ms != null) {
      const line = updated.find((l) => l.id === id);
      if (line) onScrubPreviewToMs(previewMsWhenVisible(line));
    }
  }, [textLines, onLinesChange, onScrubPreviewToMs]);

  const handleDeleteEffect = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirmingDeleteId === id) {
      const updated = textLines.filter((l) => l.id !== id);
      onLinesChange(updated);
      if (selectedEffectId === id) onSelectedEffectIdChange(null);
      setConfirmingDeleteId(null);
    } else {
      setConfirmingDeleteId(id);
      setTimeout(() => setConfirmingDeleteId((cur) => cur === id ? null : cur), 2500);
    }
  }, [confirmingDeleteId, textLines, selectedEffectId, onLinesChange, onSelectedEffectIdChange]);

  if (!selectedEffect) {
    return (
      <EditorPanePlaceholder
        realm={realm}
        messageKey="editor_select_effect_hint"
        fallback="Select an effect to edit"
      />
    );
  }

  return (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      background: "rgba(232,240,255,0.02)",
      overflow: "hidden",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        padding: "6px 12px",
        borderBottom: `1px solid ${sectionBorder}`,
        background: isWilds ? "rgba(120,200,120,0.05)" : "rgba(126,184,255,0.05)",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: tokens.effectLine,
        }}>
          {selectedEffect.type === "sound" ? "♪" : "◻"} {selectedEffect.html.replace(/<[^>]*>/g, "")}
        </span>
      </div>

      <div style={{ padding: "10px 12px", background: "rgba(232,240,255,0.02)", flex: 1, overflowY: "auto" }}>
        <LineTimingControls
          line={selectedEffect}
          realm={realm}
          onUpdate={(update) => handleEffectUpdate(selectedEffect.id, update)}
        />
      </div>
    </div>
  );
}

function InlineSceneCreatePanel({
  realm,
  titleDraft,
  submitting,
  error,
  onTitleDraftChange,
  onSubmit,
  onCancel,
}: {
  realm: RealmId;
  titleDraft: string;
  submitting: boolean;
  error: string | null;
  onTitleDraftChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";

  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        background: isWilds ? "rgba(20,40,20,0.9)" : "rgba(10,16,32,0.9)",
        border: `1px solid ${sectionBorder}`,
        borderRadius: 8,
        backdropFilter: "blur(12px)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <input
        type="text"
        value={titleDraft}
        onChange={(e) => onTitleDraftChange(e.target.value)}
        maxLength={255}
        placeholder={t(realm, "film_title_placeholder")}
        autoFocus
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
          if (e.key === "Escape") onCancel();
        }}
        style={{
          width: "100%",
          background: isWilds ? "rgba(90,180,90,0.08)" : "rgba(126,184,255,0.08)",
          border: `1px solid ${isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.3)"}`,
          borderRadius: 6,
          color: "#e8f0ff",
          padding: "7px 10px",
          fontSize: 13,
          outline: "none",
          fontFamily: "inherit",
        }}
      />
      {error ? (
        <div style={{ fontSize: 10, color: "#ff9aa2" }}>{error}</div>
      ) : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(232,240,255,0.65)",
            background: "transparent",
            border: `1px solid ${isWilds ? "rgba(90,180,90,0.25)" : "rgba(126,184,255,0.25)"}`,
            borderRadius: 6,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t(realm, "editor_cancel")}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !titleDraft.trim()}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 600,
            color: "#e8f0ff",
            background: isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)",
            border: `1px solid ${isWilds ? "rgba(90,180,90,0.5)" : "rgba(126,184,255,0.5)"}`,
            borderRadius: 6,
            cursor: submitting ? "wait" : "pointer",
            opacity: submitting ? 0.75 : 1,
            fontFamily: "inherit",
          }}
        >
          {submitting ? t(realm, "editor_ai_generating") : t(realm, "editor_create_scene")}
        </button>
      </div>
    </div>
  );
}

function sceneSelectOptionLabel(scene: SceneListItem, realm: RealmId): string {
  const suffix = scene.is_title
    ? ` (${t(realm, "editor_title_scene")})`
    : "";
  return `${scene.title}${suffix}`;
}

/* ─── Logic Editor Pane ─── */

interface LogicEditorPaneProps {
  realm: RealmId;
  line: TextLine;
  textLines: TextLine[];
  scenes: SceneListItem[];
  onLinesChange: (lines: TextLine[]) => void;
}

function LogicEditorPane({
  realm,
  line,
  textLines,
  scenes,
  onLinesChange,
}: LogicEditorPaneProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const borderColor = isWilds ? "rgba(120,200,120,0.25)" : "rgba(126,184,255,0.25)";
  const dimText = "rgba(232,240,255,0.55)";
  const logic = line.logic ?? { mode: "set" as const, variable: "" };

  const [newVarDraft, setNewVarDraft] = useState("");
  const [showNewVar, setShowNewVar] = useState(false);

  const existingVariables = useMemo(() => {
    const vars = new Set<string>();
    for (const l of textLines) {
      if (l.type === "logic" && l.logic?.variable) {
        vars.add(l.logic.variable);
      }
    }
    return Array.from(vars).sort();
  }, [textLines]);

  const existingValues = useMemo(() => {
    const vals = new Set<string>();
    for (const l of textLines) {
      if (l.type === "logic" && l.logic?.value) {
        vals.add(l.logic.value);
      }
    }
    for (const s of scenes) {
      if (s.title) vals.add(s.title);
    }
    return Array.from(vals).sort();
  }, [textLines, scenes]);

  const otherLayers = useMemo(() => {
    return textLines.filter((l) => l.id !== line.id && l.type !== "logic");
  }, [textLines, line.id]);

  const updateLogic = useCallback((patch: Partial<LogicOperation>) => {
    const updated = textLines.map((l) =>
      l.id === line.id ? { ...l, logic: { ...logic, ...patch } } : l,
    );
    onLinesChange(updated);
  }, [textLines, line.id, logic, onLinesChange]);

  const handleAddNewVar = useCallback(() => {
    const trimmed = newVarDraft.trim();
    if (!trimmed) return;
    updateLogic({ variable: trimmed });
    setNewVarDraft("");
    setShowNewVar(false);
  }, [newVarDraft, updateLogic]);

  const operators = [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "does not equal" },
    { value: "contains", label: "contains" },
    { value: "greater_than", label: "greater than" },
    { value: "less_than", label: "less than" },
  ];

  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    background: "rgba(232,240,255,0.06)",
    border: `1px solid ${borderColor}`,
    borderRadius: 6,
    padding: "6px 8px",
    color: "#e8f0ff",
    fontSize: 12,
    outline: "none",
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: "pointer",
  };

  return (
    <div style={{
      border: `1px solid rgba(232,240,255,0.12)`,
      borderRadius: 10,
      background: "rgba(232,240,255,0.02)",
      overflow: "hidden",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        padding: "6px 12px",
        borderBottom: "1px solid rgba(232,240,255,0.1)",
        background: "rgba(224,160,64,0.05)",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#e0a040",
        }}>
          {t(realm, "editor_new_logic")}
        </span>
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 12, flex: 1, overflowY: "auto" }}>
        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 4 }}>
          <button
            type="button"
            onClick={() => updateLogic({ mode: "set" })}
            style={{
              flex: 1,
              padding: "6px 0",
              borderRadius: 5,
              border: `1px solid ${logic.mode === "set" ? "#e0a040" : "rgba(232,240,255,0.15)"}`,
              background: logic.mode === "set" ? "rgba(224,160,64,0.18)" : "rgba(232,240,255,0.04)",
              color: logic.mode === "set" ? "#e0a040" : dimText,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t(realm, "editor_logic_mode_set")}
          </button>
          <button
            type="button"
            onClick={() => updateLogic({ mode: "evaluate" })}
            style={{
              flex: 1,
              padding: "6px 0",
              borderRadius: 5,
              border: `1px solid ${logic.mode === "evaluate" ? "#e0a040" : "rgba(232,240,255,0.15)"}`,
              background: logic.mode === "evaluate" ? "rgba(224,160,64,0.18)" : "rgba(232,240,255,0.04)",
              color: logic.mode === "evaluate" ? "#e0a040" : dimText,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t(realm, "editor_logic_mode_evaluate")}
          </button>
        </div>

        {/* Variable selector */}
        <div>
          <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
            {t(realm, "editor_logic_variable_label")}
          </div>
          {showNewVar ? (
            <div style={{ display: "flex", gap: 4 }}>
              <input
                type="text"
                autoFocus
                value={newVarDraft}
                onChange={(e) => setNewVarDraft(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") handleAddNewVar();
                  if (e.key === "Escape") setShowNewVar(false);
                }}
                placeholder="Variable name"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={handleAddNewVar}
                style={{
                  padding: "4px 10px",
                  borderRadius: 5,
                  border: `1px solid ${borderColor}`,
                  background: "rgba(224,160,64,0.15)",
                  color: "#e0a040",
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setShowNewVar(false)}
                style={{
                  padding: "4px 8px",
                  borderRadius: 5,
                  border: "1px solid rgba(232,240,255,0.15)",
                  background: "rgba(232,240,255,0.04)",
                  color: dimText,
                  fontSize: 10,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <select
              value={logic.variable}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setShowNewVar(true);
                  setNewVarDraft("");
                } else {
                  updateLogic({ variable: e.target.value });
                }
              }}
              style={selectStyle}
            >
              <option value="">— select —</option>
              {existingVariables.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
              <option disabled value="">────────</option>
              <option value="__new__">{t(realm, "editor_logic_new_variable")}</option>
            </select>
          )}
        </div>

        {/* Set mode: value field */}
        {logic.mode === "set" && (
          <div>
            <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
              {t(realm, "editor_logic_value_label")}
            </div>
            <input
              type="text"
              value={logic.value ?? ""}
              onChange={(e) => updateLogic({ value: e.target.value })}
              onKeyDown={(e) => e.stopPropagation()}
              list="logic-values-datalist"
              placeholder="Enter or select a value"
              style={inputStyle}
            />
            <datalist id="logic-values-datalist">
              {existingValues.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
        )}

        {/* Evaluate mode: operator + value + then-layers */}
        {logic.mode === "evaluate" && (
          <>
            <div>
              <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
                {t(realm, "editor_logic_operator_label")}
              </div>
              <select
                value={logic.operator ?? "equals"}
                onChange={(e) => updateLogic({ operator: e.target.value })}
                style={selectStyle}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
                {t(realm, "editor_logic_value_label")}
              </div>
              <input
                type="text"
                value={logic.value ?? ""}
                onChange={(e) => updateLogic({ value: e.target.value })}
                onKeyDown={(e) => e.stopPropagation()}
                list="logic-eval-values-datalist"
                placeholder="Enter or select a value"
                style={inputStyle}
              />
              <datalist id="logic-eval-values-datalist">
                {existingValues.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </div>

            <div>
              <div style={{ fontSize: 10, color: dimText, marginBottom: 6 }}>
                {t(realm, "editor_logic_then_layers")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {otherLayers.length === 0 ? (
                  <div style={{ fontSize: 11, color: "rgba(232,240,255,0.35)", fontStyle: "italic" }}>
                    No other layers to activate
                  </div>
                ) : (
                  otherLayers.map((layer) => {
                    const checked = (logic.then_layer_ids ?? []).includes(layer.id);
                    const layerLabel = layer.html.replace(/<[^>]*>/g, "").trim().slice(0, 40) || `(${layer.type ?? "text"})`;
                    return (
                      <label
                        key={layer.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 11,
                          color: checked ? "#e8f0ff" : dimText,
                          cursor: "pointer",
                          padding: "3px 6px",
                          borderRadius: 4,
                          background: checked ? "rgba(224,160,64,0.1)" : "transparent",
                          border: `1px solid ${checked ? "rgba(224,160,64,0.3)" : "transparent"}`,
                          transition: "all 0.12s",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const current = logic.then_layer_ids ?? [];
                            const next = checked
                              ? current.filter((id) => id !== layer.id)
                              : [...current, layer.id];
                            updateLogic({ then_layer_ids: next });
                          }}
                          style={{ accentColor: "#e0a040" }}
                        />
                        {layerLabel}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Choices Tab ─── */

function ChoicesTabContent({
  scene,
  story,
  choices,
  storedChoices,
  scenes,
  realm,
  totalDurationMs,
  gotoSceneId,
  onGotoSceneIdChange,
  sceneDurationSec,
  selectedChoiceId,
  choiceActionIdsRef,
  onChoicesChange,
  onScenesChanged,
  onStoryStructureChange,
  onScrubPreviewToMs,
  onSelectChoice,
  onSelectedChoiceIdChange,
  persistChoiceProperties,
  onChoiceArrowResize,
}: {
  scene: SceneListItem;
  story: StoryListItem | null;
  choices: SceneChoice[];
  storedChoices: SceneChoice[];
  scenes: SceneListItem[];
  realm: RealmId;
  totalDurationMs: number;
  gotoSceneId: string | null;
  onGotoSceneIdChange: (id: string | null) => void;
  sceneDurationSec: number | null;
  selectedChoiceId: string | null;
  choiceActionIdsRef: MutableRefObject<Record<string, string>>;
  onChoicesChange: (choices: SceneChoice[]) => void;
  onScenesChanged?: (scenes: SceneListItem[]) => void;
  onStoryStructureChange?: () => void;
  onScrubPreviewToMs: (ms: number) => void;
  onSelectChoice: (choiceId: string) => void;
  onSelectedChoiceIdChange: (id: string | null) => void;
  persistChoiceProperties: (choice: SceneChoice, options?: { refreshGraph?: boolean; immediate?: boolean }) => void;
  onChoiceArrowResize: (choiceId: string, scale: number, rotation: number) => void;
}) {
  const isWilds = realm === "wilds";
  const sectionBorder = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";
  const sectionLabelColor = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const borderColor = isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.25)";
  const dimText = "rgba(232,240,255,0.5)";

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [linkCreateTarget, setLinkCreateTarget] = useState<
    { kind: "choice"; choiceId: string } | { kind: "goto" } | null
  >(null);
  const [linkCreateTitle, setLinkCreateTitle] = useState("");
  const [linkCreateSubmitting, setLinkCreateSubmitting] = useState(false);
  const [linkCreateError, setLinkCreateError] = useState<string | null>(null);

  const effectiveDurationMs = useMemo(
    () => computeEffectiveSceneDuration(totalDurationMs, choices, sceneDurationSec),
    [totalDurationMs, choices, sceneDurationSec],
  );
  const fallbackAppearMs = Math.max(0, effectiveDurationMs - 3000);
  const branchChoiceCount = choices.filter((choice) => !isViewerExitChoice(choice)).length;
  const canAddChoice = branchChoiceCount < 2;

  const selectedChoice = choices.find((c) => c.id === selectedChoiceId) ?? null;
  const selectedChoiceIndex = selectedChoice
    ? choices.findIndex((c) => c.id === selectedChoice.id)
    : -1;

  const resolveActionId = (choiceId: string) =>
    choiceActionIdsRef.current[choiceId] ?? choiceId;

  const handleAddChoice = async () => {
    if (!story || branchChoiceCount >= 2) return;
    const arrowDefaults = loadStoryChoiceArrowDefaults(story.id);
    const timing = {
      appear_at_ms: fallbackAppearMs,
      fade_in_ms: 400,
      duration_ms: 2000,
      fade_out_ms: 400,
    };
    const seconds = timingToChoiceSeconds(timing);
    try {
      const action = await api.sceneActions.create({
        scene_id: scene.id,
        name: t(realm, "editor_choice_label", "Choice"),
        type: "choice",
        order: choices.length,
        duration: 0,
        properties: {
          label: "",
          target_scene_id: scenes[0]?.id ?? "",
          x: arrowDefaults.x,
          y: arrowDefaults.y,
          w: arrowDefaults.w,
          h: arrowDefaults.h,
          arrow_rotation: arrowDefaults.arrow_rotation,
          arrow_scale: arrowDefaults.arrow_scale,
          fade_in_ms: timing.fade_in_ms,
          fade_out_ms: timing.fade_out_ms,
          duration_ms: timing.duration_ms,
        },
      });
      choiceActionIdsRef.current[action.id] = action.id;
      const newChoice: SceneChoice = {
        id: action.id,
        label: "",
        choice_type: "default",
        choice_icon: null,
        choice_icon_media_id: null,
        choice_icon_url: null,
        reveal_mode: "immediate",
        start_time_seconds: seconds.start_time_seconds,
        pause_for_choice: false,
        end_time_seconds: seconds.end_time_seconds,
        target_scene_id: scenes[0]?.id ?? "",
        x: arrowDefaults.x,
        y: arrowDefaults.y,
        w: arrowDefaults.w,
        h: arrowDefaults.h,
        arrow_rotation: arrowDefaults.arrow_rotation,
        arrow_scale: arrowDefaults.arrow_scale,
        fade_in_ms: timing.fade_in_ms,
        fade_out_ms: timing.fade_out_ms,
        duration_ms: timing.duration_ms,
        visibility_rules: null,
        state_updates: [],
      };
      onChoicesChange([...storedChoices, newChoice]);
      onSelectedChoiceIdChange(newChoice.id);
      onScrubPreviewToMs(previewMsWhenVisible(timing));
      onStoryStructureChange?.();
    } catch {
      // silent
    }
  };

  const createExitSuppressionMarker = async (): Promise<SceneChoice> => {
    const action = await api.sceneActions.create({
      scene_id: scene.id,
      name: t(realm, "editor_exit_choice_label", "Go To The End"),
      type: "choice",
      order: 999,
      properties: {
        label: "",
        target_scene_id: VIEWER_EXIT_SUPPRESSED_ID,
        pause_for_choice: false,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        arrow_rotation: 0,
        arrow_scale: 1,
      },
    });
    choiceActionIdsRef.current[action.id] = action.id;
    return {
      id: action.id,
      label: "",
      choice_type: "default",
      choice_icon: null,
      choice_icon_media_id: null,
      choice_icon_url: null,
      reveal_mode: "immediate",
      start_time_seconds: null,
      pause_for_choice: false,
      end_time_seconds: null,
      target_scene_id: VIEWER_EXIT_SUPPRESSED_ID,
      x: 0,
      y: 0,
      w: 0,
      h: 0,
      arrow_rotation: 0,
      arrow_scale: 1,
      fade_in_ms: null,
      fade_out_ms: null,
      duration_ms: null,
      visibility_rules: null,
      state_updates: [],
    };
  };

  const persistSyntheticExitChoice = async (choice: SceneChoice): Promise<SceneChoice> => {
    if (choice.id !== VIEWER_EXIT_CHOICE_ID) return choice;
    const action = await api.sceneActions.create({
      scene_id: scene.id,
      name: t(realm, "editor_exit_choice_label", "Go To The End"),
      type: "choice",
      order: storedChoices.length,
      properties: choiceToActionProperties(choice),
    });
    choiceActionIdsRef.current[action.id] = action.id;
    const persisted = { ...choice, id: action.id };
    onChoicesChange([
      ...storedChoices.filter((c) => !isViewerExitSuppressionMarker(c)),
      persisted,
    ]);
    onStoryStructureChange?.();
    return persisted;
  };

  const handleRemoveChoice = async (choiceId: string) => {
    const choice = choices.find((c) => c.id === choiceId);
    if (choice && isViewerExitChoice(choice)) {
      try {
        if (choice.id !== VIEWER_EXIT_CHOICE_ID) {
          await api.sceneActions.destroy(resolveActionId(choiceId));
          delete choiceActionIdsRef.current[choiceId];
        }
        let nextStored = storedChoices.filter((c) => c.id !== choiceId);
        if (!nextStored.some(isViewerExitSuppressionMarker)) {
          const marker = await createExitSuppressionMarker();
          nextStored = [...nextStored.filter((c) => !isViewerExitSuppressionMarker(c)), marker];
        }
        onChoicesChange(nextStored);
        if (selectedChoiceId === choiceId) onSelectedChoiceIdChange(null);
        setConfirmingDeleteId(null);
        onStoryStructureChange?.();
      } catch {
        // silent
      }
      return;
    }

    try {
      await api.sceneActions.destroy(resolveActionId(choiceId));
      delete choiceActionIdsRef.current[choiceId];
      onChoicesChange(storedChoices.filter((c) => c.id !== choiceId));
      if (selectedChoiceId === choiceId) onSelectedChoiceIdChange(null);
      setConfirmingDeleteId(null);
      onStoryStructureChange?.();
    } catch {
      // silent
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, choiceId: string) => {
    e.stopPropagation();
    if (confirmingDeleteId === choiceId) {
      void handleRemoveChoice(choiceId);
    } else {
      setConfirmingDeleteId(choiceId);
      setTimeout(() => setConfirmingDeleteId((cur) => (cur === choiceId ? null : cur)), 2500);
    }
  };

  const handleChoiceTargetChange = async (choiceId: string, targetId: string) => {
    const updated = storedChoices.map((c) =>
      c.id === choiceId ? { ...c, target_scene_id: targetId } : c,
    );
    onChoicesChange(updated);
    const changed = updated.find((c) => c.id === choiceId);
    if (changed) persistChoiceProperties(changed, { refreshGraph: true, immediate: true });
  };

  const handleChoiceLabelChange = async (choiceId: string, label: string) => {
    const choice = choices.find((c) => c.id === choiceId);
    if (!choice) return;
    try {
      let workingChoice = { ...choice, label };
      if (isViewerExitChoice(choice) && choice.id === VIEWER_EXIT_CHOICE_ID) {
        workingChoice = await persistSyntheticExitChoice(workingChoice);
        if (selectedChoiceId === VIEWER_EXIT_CHOICE_ID) {
          onSelectedChoiceIdChange(workingChoice.id);
        }
      } else {
        const updated = storedChoices.map((c) =>
          c.id === choiceId ? { ...c, label } : c,
        );
        onChoicesChange(updated);
        workingChoice = updated.find((c) => c.id === choiceId) ?? workingChoice;
      }
      persistChoiceProperties(workingChoice, { immediate: true });
    } catch {
      // silent
    }
  };

  const handleCreateLinkedScene = useCallback(async () => {
    if (!story || !linkCreateTarget) return;
    const trimmedTitle = linkCreateTitle.trim();
    if (!trimmedTitle || linkCreateSubmitting) return;

    setLinkCreateSubmitting(true);
    setLinkCreateError(null);
    try {
      const created = await api.scenes.create({
        story_id: story.id,
        title: trimmedTitle,
      });
      const refreshed = await api.scenes.index(story.id);
      onScenesChanged?.(refreshed);

      if (linkCreateTarget.kind === "choice") {
        await handleChoiceTargetChange(linkCreateTarget.choiceId, created.id);
      } else {
        await onGotoSceneIdChange(created.id);
      }

      setLinkCreateTarget(null);
      setLinkCreateTitle("");
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setLinkCreateError(body?.error ?? body?.message ?? `${err.status}`);
      } else {
        setLinkCreateError(err instanceof Error ? err.message : "error");
      }
    } finally {
      setLinkCreateSubmitting(false);
    }
  }, [
    story,
    linkCreateTarget,
    linkCreateTitle,
    linkCreateSubmitting,
    onScenesChanged,
    onGotoSceneIdChange,
    choices,
  ]);

  useEffect(() => {
    if (
      linkCreateTarget?.kind === "choice" &&
      linkCreateTarget.choiceId !== selectedChoiceId
    ) {
      setLinkCreateTarget(null);
      setLinkCreateTitle("");
      setLinkCreateError(null);
    }
  }, [selectedChoiceId, linkCreateTarget]);

  const sceneSelectStyle = {
    width: "100%",
    background: "rgba(232,240,255,0.06)",
    border: `1px solid ${borderColor}`,
    borderRadius: 6,
    padding: "6px 8px",
    color: "#e8f0ff",
    fontSize: 12,
    outline: "none",
    cursor: "pointer",
  } as const;

  const handleChoiceTimingUpdate = async (choiceId: string, update: Partial<TextLine>) => {
    let choice = choices.find((c) => c.id === choiceId);
    if (!choice) return;
    const current = resolveChoiceTiming(choice, fallbackAppearMs);
    const nextTiming = {
      appear_at_ms: update.appear_at_ms ?? current.appear_at_ms,
      fade_in_ms: update.fade_in_ms ?? current.fade_in_ms,
      duration_ms: update.duration_ms ?? current.duration_ms,
      fade_out_ms: update.fade_out_ms ?? current.fade_out_ms,
    };
    const seconds = timingToChoiceSeconds(nextTiming);
    const timingPatch: Record<string, unknown> = {
      start_time_seconds: seconds.start_time_seconds,
      end_time_seconds: seconds.end_time_seconds,
      fade_in_ms: nextTiming.fade_in_ms,
      fade_out_ms: nextTiming.fade_out_ms,
      duration_ms: nextTiming.duration_ms,
    };
    if (update.persist !== undefined) {
      timingPatch.pause_for_choice = update.persist;
    }

    try {
      if (isViewerExitChoice(choice) && choice.id === VIEWER_EXIT_CHOICE_ID) {
        choice = await persistSyntheticExitChoice({ ...choice, ...timingPatch } as typeof choice);
        if (selectedChoiceId === VIEWER_EXIT_CHOICE_ID) {
          onSelectedChoiceIdChange(choice.id);
        }
      } else {
        const updated = storedChoices.map((c) =>
          c.id === choiceId ? { ...c, ...timingPatch } as typeof c : c,
        );
        onChoicesChange(updated);
        choice = updated.find((c) => c.id === choiceId) ?? choice;
      }
      persistChoiceProperties(choice);
      if (update.appear_at_ms != null) {
        onScrubPreviewToMs(previewMsWhenVisible(resolveChoiceTiming(choice, fallbackAppearMs)));
      }
    } catch {
      // silent
    }
  };

  const choicesListPane = (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      padding: 12,
      background: "rgba(232,240,255,0.02)",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      flex: 1,
    }}>
      <div style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: sectionLabelColor,
        marginBottom: 8,
        flexShrink: 0,
      }}>
        {t(realm, "editor_choices", "Choices")}
      </div>

      {choices.length > 0 && gotoSceneId ? (
        <div style={{
          fontSize: 10,
          color: "rgba(232,240,255,0.4)",
          marginBottom: 8,
          lineHeight: 1.4,
          flexShrink: 0,
        }}>
          {t(realm, "editor_goto_nullified", "Disabled — choices override goto")}
        </div>
      ) : null}

      <div style={{ overflowY: "auto", minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
        {choices.length === 0 ? (
          <div style={{ textAlign: "center", color: "rgba(232,240,255,0.35)", fontSize: 12, padding: "8px 0" }}>
            {t(realm, "editor_choices_empty_hint", "Set a goto scene or add a choice to create branching paths.")}
          </div>
        ) : null}

        {choices.map((choice, index) => {
          const timing = resolveChoiceTiming(choice, fallbackAppearMs);
          const targetScene = scenes.find((s) => s.id === choice.target_scene_id);
          const isSelected = selectedChoiceId === choice.id;
          return (
            <div
              key={choice.id}
              onClick={() => onSelectChoice(choice.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 6,
                background: isSelected ? `${accent}18` : "rgba(232,240,255,0.03)",
                border: `1px solid ${isSelected ? accent : "rgba(232,240,255,0.12)"}`,
                fontSize: 12,
                color: accent,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: 8, opacity: 0.7 }}>{index + 1}</span>
              <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#e8f0ff" }}>
                {isViewerExitChoice(choice)
                  ? (choice.label
                    ? t(realm, choice.label, choice.label)
                    : t(realm, "editor_exit_choice_label", "Go To The End"))
                  : (targetScene?.title ?? t(realm, "editor_choice_label", "Choice"))}
              </div>
              <div style={{ fontSize: 9, color: "rgba(232,240,255,0.45)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                {formatMs(timing.appear_at_ms)}
              </div>
              <button
                type="button"
                onClick={(e) => handleDeleteClick(e, choice.id)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  border: confirmingDeleteId === choice.id
                    ? "1px solid rgba(255,107,107,0.7)"
                    : "1px solid transparent",
                  background: confirmingDeleteId === choice.id
                    ? "rgba(255,107,107,0.2)"
                    : "rgba(255,107,107,0.1)",
                  color: confirmingDeleteId === choice.id
                    ? "#ff8d8d"
                    : "rgba(255,150,150,0.7)",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s",
                  flexShrink: 0,
                }}
                title={confirmingDeleteId === choice.id
                  ? t(realm, "editor_delete_confirm", "Click again to confirm")
                  : t(realm, "editor_remove_choice", "Remove Choice")}
              >
                {confirmingDeleteId === choice.id ? "?" : "×"}
              </button>
            </div>
          );
        })}
      </div>

      {canAddChoice ? (
        <button
          type="button"
          onClick={() => void handleAddChoice()}
          style={{
            marginTop: 6,
            width: "100%",
            padding: "6px 0",
            borderRadius: 6,
            border: `1px dashed ${isWilds ? "rgba(120,200,120,0.3)" : "rgba(126,184,255,0.3)"}`,
            background: "transparent",
            color: accent,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.02em",
            flexShrink: 0,
          }}
        >
          + {t(realm, "editor_flow_add_choice_here", "Add Choice Here")}
        </button>
      ) : null}
    </div>
  );

  const gotoSceneEditorPane = (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      padding: 12,
      background: "rgba(232,240,255,0.02)",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: sectionLabelColor,
        marginBottom: 8,
        flexShrink: 0,
      }}>
        {t(realm, "editor_goto_scene", "Goto Scene")}
      </div>
      <select
        value={gotoSceneId ?? ""}
        onChange={(e) => {
          if (e.target.value === CREATE_SCENE_OPTION) {
            setLinkCreateTarget({ kind: "goto" });
            setLinkCreateTitle("");
            setLinkCreateError(null);
            return;
          }
          void onGotoSceneIdChange(e.target.value || null);
        }}
        style={sceneSelectStyle}
      >
        <option value="">{t(realm, "editor_goto_none", "None")}</option>
        {scenes.map((s) => (
          <option key={s.id} value={s.id}>
            {sceneSelectOptionLabel(s, realm)}
          </option>
        ))}
        <option disabled value="">────────</option>
        <option value={CREATE_SCENE_OPTION}>
          + {t(realm, "editor_create_scene_plain")}
        </option>
      </select>
      {linkCreateTarget?.kind === "goto" ? (
        <InlineSceneCreatePanel
          realm={realm}
          titleDraft={linkCreateTitle}
          submitting={linkCreateSubmitting}
          error={linkCreateError}
          onTitleDraftChange={setLinkCreateTitle}
          onSubmit={() => void handleCreateLinkedScene()}
          onCancel={() => {
            setLinkCreateTarget(null);
            setLinkCreateTitle("");
            setLinkCreateError(null);
          }}
        />
      ) : null}
    </div>
  );

  const choiceEditorPane = selectedChoice ? (
    <div style={{
      border: `1px solid ${sectionBorder}`,
      borderRadius: 10,
      background: "rgba(232,240,255,0.02)",
      overflow: "hidden",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        padding: "6px 12px",
        borderBottom: `1px solid ${sectionBorder}`,
        background: isWilds ? "rgba(120,200,120,0.05)" : "rgba(126,184,255,0.05)",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: accent,
        }}>
          {t(realm, "editor_choice_label", "Choice")} {selectedChoiceIndex + 1}
        </span>
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10, flex: 1, overflowY: "auto" }}>
        <div>
          <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
            {t(realm, "editor_choice_display_label", "Arrow Label")}
          </div>
          <input
            type="text"
            value={selectedChoice.label}
            placeholder={t(realm, "editor_choice_display_label_empty", "No label")}
            onChange={(e) => { void handleChoiceLabelChange(selectedChoice.id, e.target.value); }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: "rgba(232,240,255,0.06)",
              border: `1px solid ${borderColor}`,
              borderRadius: 6,
              padding: "6px 8px",
              color: "#e8f0ff",
              fontSize: 12,
              outline: "none",
            }}
          />
          <div style={{ fontSize: 10, color: dimText, marginTop: 4, lineHeight: 1.4 }}>
            {t(
              realm,
              "editor_choice_display_label_hint",
              "Leave blank to hide the label beneath the arrow. Lexicon keys like viewer_continue resolve to realm text.",
            )}
          </div>
          {selectedChoice.label ? (
            <div style={{ fontSize: 10, color: accent, marginTop: 4 }}>
              {t(realm, "editor_choice_display_label_preview", "Preview")}: {t(realm, selectedChoice.label, selectedChoice.label)}
            </div>
          ) : null}
        </div>

        <div>
          <div style={{ fontSize: 10, color: dimText, marginBottom: 4 }}>
            {t(realm, "editor_choice_target", "Destination Scene")}
          </div>
          {isViewerExitChoice(selectedChoice) ? (
            <div style={{ fontSize: 11, color: dimText, lineHeight: 1.4 }}>
              {t(realm, "editor_choice_exit_target_hint", "Terminal exit — ends the story for viewers.")}
            </div>
          ) : (
          <select
            value={selectedChoice.target_scene_id}
            onChange={(e) => {
              if (e.target.value === CREATE_SCENE_OPTION) {
                setLinkCreateTarget({ kind: "choice", choiceId: selectedChoice.id });
                setLinkCreateTitle("");
                setLinkCreateError(null);
                return;
              }
              void handleChoiceTargetChange(selectedChoice.id, e.target.value);
            }}
            style={sceneSelectStyle}
          >
            {scenes.map((s) => (
              <option key={s.id} value={s.id}>
                {sceneSelectOptionLabel(s, realm)}
              </option>
            ))}
            <option disabled value="">────────</option>
            <option value={CREATE_SCENE_OPTION}>
              + {t(realm, "editor_create_scene_plain")}
            </option>
          </select>
          )}
          {linkCreateTarget?.kind === "choice" &&
          linkCreateTarget.choiceId === selectedChoice.id ? (
            <InlineSceneCreatePanel
              realm={realm}
              titleDraft={linkCreateTitle}
              submitting={linkCreateSubmitting}
              error={linkCreateError}
              onTitleDraftChange={setLinkCreateTitle}
              onSubmit={() => void handleCreateLinkedScene()}
              onCancel={() => {
                setLinkCreateTarget(null);
                setLinkCreateTitle("");
                setLinkCreateError(null);
              }}
            />
          ) : null}
        </div>

        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "8px 10px",
          borderRadius: 6,
          border: "1px solid rgba(232,240,255,0.1)",
          background: "rgba(232,240,255,0.03)",
        }}>
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: sectionLabelColor,
          }}>
            {t(realm, "editor_arrow_tool", "Place Arrow")}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 9, color: dimText, minWidth: 52, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {t(realm, "editor_arrow_scale", "Scale")}
            </span>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={selectedChoice.arrow_scale}
              onChange={(e) => onChoiceArrowResize(selectedChoice.id, parseFloat(e.target.value), selectedChoice.arrow_rotation)}
              style={{ flex: 1, accentColor: accent }}
            />
            <span style={{ fontSize: 10, color: dimText, minWidth: 28, fontVariantNumeric: "tabular-nums" }}>
              {selectedChoice.arrow_scale.toFixed(1)}×
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 9, color: dimText, minWidth: 52, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {t(realm, "editor_arrow_rotation", "Rotation")}
            </span>
            <input
              type="range"
              min={0}
              max={359}
              step={1}
              value={Math.round(selectedChoice.arrow_rotation)}
              onChange={(e) => onChoiceArrowResize(selectedChoice.id, selectedChoice.arrow_scale, parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: accent }}
            />
            <span style={{ fontSize: 10, color: dimText, minWidth: 28, fontVariantNumeric: "tabular-nums" }}>
              {Math.round(selectedChoice.arrow_rotation)}°
            </span>
          </div>
          <div style={{ fontSize: 9, color: "rgba(232,240,255,0.35)", letterSpacing: "0.04em" }}>
            {t(realm, "editor_arrow_rotate_hint", "Drag the handle above the arrow in preview to rotate")}
          </div>
        </div>
      </div>

      <div style={{ padding: "10px 12px", borderTop: "1px solid rgba(232,240,255,0.1)", background: "rgba(232,240,255,0.02)", flexShrink: 0 }}>
        <LineTimingControls
          line={{
            ...choiceTimingToLine(
              selectedChoice.id,
              resolveChoiceTiming(selectedChoice, fallbackAppearMs),
            ),
            persist: isViewerExitChoice(selectedChoice) ? true : (selectedChoice.pause_for_choice !== false),
          }}
          realm={realm}
          onUpdate={(update) => {
            if (update.persist !== undefined && isViewerExitChoice(selectedChoice)) return;
            handleChoiceTimingUpdate(selectedChoice.id, update);
          }}
        />
      </div>
    </div>
  ) : (
    <EditorPanePlaceholder
      realm={realm}
      messageKey="editor_select_choice_hint"
      fallback="Select a choice to edit"
    />
  );

  if (choices.length === 0) return gotoSceneEditorPane;
  return choiceEditorPane;
}

/* ─── Settings Tab ─── */

const SCENE_COLORS = [
  null, "#d31e2f", "#e06c24", "#e0c024", "#3ea63e",
  "#2e8bcc", "#7b4fcc", "#cc4fa0", "#888888",
];


/* ─── Shared primitives ─── */

function SectionLabel({ text }: { realm: RealmId; text: string }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(232,240,255,0.5)" }}>
      {text}
    </div>
  );
}


function LoadingIndicator({ realm }: { realm: RealmId }) {
  const accent = realm === "wilds" ? "rgba(120,200,120,0.6)" : "rgba(126,184,255,0.6)";
  return (
    <div style={{ textAlign: "center", padding: 40, color: accent, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>
      Loading…
    </div>
  );
}
